import React, { useState, useRef, useEffect } from 'react';
import PlayBuilderActionButtons from './PlayBuilderActionButtons.tsx';
import DialogPlayAnimation from './DialogPlayAnimation.tsx';
import PhasesSectionPlayBuilder from './PhasesSectionPlayBuilder.tsx';
import PlayAnimationLinesOrder from './PlayAnimationLinesOrder.tsx';
import PlayBuilderBottomBar from './PlayBuilderBottomBar.tsx';
import { Button, IconButton, Typography, Box, Divider, ToggleButtonGroup, ToggleButton, Snackbar, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, useTheme, TextField, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import CloseIcon from '@mui/icons-material/Close';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { courtDimensions, getPlayerLinesInOrder, generatePathD, FRAME_RENDER_CONFIG } from './Functions_Board_Grapchics.tsx';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Court from './Court.tsx';
import ConeIcon from './ConeIcon.tsx';
// import LanguageSwitcher from '../components/LanguageSwitcher.js';
import { app } from '../constants/constants.js';
import { generatePDFPlay } from './Functions_PDF.tsx';

type CourtType = 'halfcourt' | 'fullcourt';
type OperationMode = 'play' | 'exercise';

// Interfaces
export interface Player {
    id: number;
    x: number;
    y: number;
    number: number;
    hasBall: boolean;
    isDefender?: boolean;
    isSpecialDefender?: boolean;
    isCoach?: boolean;
    color?: string;
    armRotation?: number; // Rotation angle for special defender arms (in degrees, 0-360)
}

export interface Cone {
    id: number;
    x: number;
    y: number;
    color: string;
}

export interface TextBox {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    text: string;
    fontSize?: number;
}

export interface Shade {
    id: number;
    shape: 'circle' | 'rect';
    x: number;
    y: number;
    width: number;
    height: number;
    color?: string; // base color (hex or simple name); opacity is hardcoded in render
}

export interface Frame {
    id: number;
    players: Player[];
    lines: Line[];
    cones: Cone[];
    texts: TextBox[];
    shades: Shade[];
    description?: string;
}

export interface Line {
    uniqueId: string;
    id: number;
    fromId: number;
    toId?: number;
    type: 'straight' | 'zigzag' | 'dashed' | 'bar' | 'shot' | 'handoff';
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    controlX: number;
    controlY: number;
    midX?: number;
    midY?: number;
    controlX2?: number;
    controlY2?: number;
    order?: number;
    sameMomentAsPrevious?: boolean;
    controlModified?: boolean;
    control2Modified?: boolean;
    color?: string;
    handoffColor?: string; // Color for the horizontal lines in handoff
}

export interface Play {
    frames: Frame[];
    currentFrameIndex: number;
    dragging: DraggingState;
    selectedPlayerId: number | null;
    isModalOpen: boolean;
    nextPlayerId: number;
    nextLineId: number;
    nextConeId: number; // Add for unique cone IDs
    courtType: 'halfcourt' | 'fullcourt';
}

interface DraggingState {
    isDragging: boolean;
    type: 'player' | 'end' | 'control' | 'control2' | 'mid' | 'cone' | 'armRotation' | 'text' | 'textResize' | 'shade' | 'shadeResize' | null; // Add 'armRotation'
    id: number | null;
}

const DRAGGINGSTATE: DraggingState = { isDragging: false, type: null, id: null };
const FRAMEINIT: Frame = {
    id: 1,
    players: [
        { id: 1, x: 272, y: 350, number: 1, hasBall: true },
        { id: 2, x: 130, y: 280, number: 2, hasBall: false },
        { id: 3, x: 420, y: 280, number: 3, hasBall: false },
        { id: 4, x: 360, y: 150, number: 4, hasBall: false },
        { id: 5, x: 180, y: 150, number: 5, hasBall: false },
    ],
    lines: [],
    cones: [],
    texts: [],
    shades: [],
    description: '',
};
const FRAMEINITEXERCISE: Frame = { ...FRAMEINIT };

interface PropsDialogPlayBuilder {
    title?: string;
    playDetails?: PlayDetails | null;
    portrait?: boolean;
    nativeApp?: boolean;
    operationMode?: OperationMode;
    use3DPlayers?: boolean;
    courtColors?: {
        lineColor?: string;
        paintAreaColor?: string | null;
        paintAreaUseWoodPattern?: boolean;
        midRangeColor?: string | null;
        midRangeUseWoodPattern?: boolean;
        outOfBoundsColor?: string | null;
        outOfBoundsUseWoodPattern?: boolean;
    };
}

export interface PlayDetails {
    frames: Frame[];
    currentFrameIndex: number;
    dragging: DraggingState;
    selectedPlayerId: number | null;
    selectedLineId: number | null;
    courtType: 'halfcourt' | 'fullcourt';
}

// Main Component
// export default function DialogPlayBuilder({ play, open, onClose }: PropsDialogPlayBuilder) {
export default function DialogPlayBuilder(props: PropsDialogPlayBuilder) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const playMainTitle = (theme as any).palette?.custom?.playMainTitle ?? '#00398F';
    const playBackground = (theme as any).palette?.custom?.playBackground ?? '#F6F8FB';
    const playBorder = (theme as any).palette?.custom?.playBorder ?? 'rgba(0,0,0,0.08)';

    const isPortraitMedia = useMediaQuery('(orientation: portrait)');
    const isPortrait = props.portrait ?? isPortraitMedia;
    const nativeApp = props.nativeApp ?? false;
    const operationMode = props.operationMode ?? 'play';
    const use3DPlayers = props.use3DPlayers ?? false;
    const courtColors = props.courtColors;

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
    const showSnackbar = (msgKey: string) => {
        setSnackbar({ open: true, message: t(msgKey) });
    };

    const [courtType, setCourtType] = useState<CourtType>('halfcourt');
    const [initialPointerPosition, setInitialPointerPosition] = useState<{ x: number; y: number } | null>(null);
    const { width: courtWidth, height: courtHeight } = courtDimensions[courtType];
    const MAX_SVG_WIDTH = courtType === 'halfcourt' ? 600 : 800;
    const [frames, setFrames] = useState<Frame[]>([JSON.parse(JSON.stringify(FRAMEINIT))]);
    const [currentFrameIndex, setCurrentFrameIndex] = useState<number>(0);
    const [dragging, setDragging] = useState<DraggingState>(DRAGGINGSTATE);
    const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [nextConeId, setNextConeId] = useState<number>(1);
    const [nextLineId, setNextLineId] = useState<number>(1);
    const [scale, setScale] = useState<number>(props.portrait ? 1.25 : (isPortraitMedia ? 1.25 : 1.0));
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
    const [selectedConeId, setSelectedConeId] = useState<number | null>(null);
    const [selectedTextBoxId, setSelectedTextBoxId] = useState<number | null>(null);
    const [selectedShadeId, setSelectedShadeId] = useState<number | null>(null);
    const [isSignInPromptOpen, setIsSignInPromptOpen] = useState<boolean>(false);
    const [isConfirmExitDialogOpen, setIsConfirmExitDialogOpen] = useState<boolean>(false);
    const [isPdfNameDialogOpen, setIsPdfNameDialogOpen] = useState<boolean>(false);
    const [pdfPlayName, setPdfPlayName] = useState<string>('');
    const [pdfUse3DPlayers, setPdfUse3DPlayers] = useState<boolean>(true);
    const [nextShadeId, setNextShadeId] = useState<number>(1);
    const [shadeDragOffset, setShadeDragOffset] = useState<{ dx: number; dy: number } | null>(null);
    const [shadeResizeStart, setShadeResizeStart] = useState<{
        startX: number; startY: number; x: number; y: number; width: number; height: number;
    } | null>(null);
    const [shadeResizeCorner, setShadeResizeCorner] = useState<'tl' | 'tr' | 'bl' | 'br' | null>(null);
    const [nextTextBoxId, setNextTextBoxId] = useState<number>(1);
    const [textDragOffset, setTextDragOffset] = useState<{ dx: number; dy: number } | null>(null);
    const [textResizeStart, setTextResizeStart] = useState<{
        startX: number; startY: number; x: number; y: number; width: number; height: number;
    } | null>(null);
    const [textResizeCorner, setTextResizeCorner] = useState<'tl' | 'tr' | 'bl' | 'br' | null>(null);
    const pendingTextFocusIdRef = useRef<number | null>(null);
    const textAreaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
    const [isDraggingLineEnd, setIsDraggingLineEnd] = useState<boolean>(false);
    const [showDescription, setShowDescription] = useState<boolean>(false);
    const [hoveredLineId, setHoveredLineId] = useState<number | null>(null);

    const COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER = '#9333ea';

    const getMaxShadeId = (framesToScan: Frame[]) => {
        const all = framesToScan.flatMap((f) => (f.shades || []).map((s) => s.id));
        return all.length ? Math.max(...all) : 0;
    };

    const getMaxTextBoxId = (framesToScan: Frame[]) => {
        const all = framesToScan.flatMap((f) => (f.texts || []).map((tb) => tb.id));
        return all.length ? Math.max(...all) : 0;
    };

    // --- Shade color helpers (keep in sync with export renderer as best effort) ---
    const normalizeShadeColor = (color?: string | null): string => {
        const fallback = '#38BDF8';
        if (!color) return fallback;
        const c = String(color).trim();
        if (!c) return fallback;
        const lower = c.toLowerCase();
        const NAMED: Record<string, string> = {
            black: '#000000',
            blue: '#0000FF',
            red: '#FF0000',
            green: '#008000',
            purple: '#800080',
            orange: '#FFA500',
            yellow: '#FFFF00',
            navy: '#000080',
            grey: '#808080',
            gray: '#808080',
            cyan: '#38BDF8',
            teal: '#14B8A6',
        };
        if (NAMED[lower]) return NAMED[lower];
        const m = lower.match(/^#([\da-f]{3}|[\da-f]{6})$/i);
        if (!m) return fallback;
        let hex = m[1];
        if (hex.length === 3) hex = hex.split('').map((ch) => ch + ch).join('');
        return `#${hex.toUpperCase()}`;
    };

    const hexToRgba = (hex: string, alpha: number) => {
        const h = normalizeShadeColor(hex).slice(1);
        const num = parseInt(h, 16);
        const r = (num >> 16) & 255;
        const g = (num >> 8) & 255;
        const b = num & 255;
        const a = Math.max(0, Math.min(1, alpha));
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    };

    const darkenHex = (hex: string, amount: number) => {
        const h = normalizeShadeColor(hex).slice(1);
        const num = parseInt(h, 16);
        const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
        const factor = Math.max(0, Math.min(1, 1 - amount));
        const r = clamp(((num >> 16) & 255) * factor);
        const g = clamp(((num >> 8) & 255) * factor);
        const b = clamp((num & 255) * factor);
        const combined = (r << 16) | (g << 8) | b;
        return `#${combined.toString(16).padStart(6, '0').toUpperCase()}`;
    };

    const colorOptions = [
        { name: 'Black', value: 'black' },
        { name: 'Blue', value: 'blue' },
        { name: 'Red', value: 'red' },
        { name: 'Green', value: 'green' },
        { name: 'Purple', value: 'purple' },
    ];

    // Common styles for consistency
    const commonStyles = {
        card: {
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            transition: 'all 0.2s ease-in-out',
        },
        cardHover: {
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            borderColor: 'rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)',
        },
        button: {
            textTransform: 'none',
            fontWeight: 'bold',
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
        },
        buttonHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
        },
        toggleButton: {
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: '12px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                backgroundColor: '#f8f9fa',
                transform: 'translateY(-1px)',
            }
        }
    };

    const toggleShape = () => {
        if (selectedLineId === null) return showSnackbar('PlayBuilder04');
        const selectedLine = currentFrame.lines.find((line) => line.id === selectedLineId);
        if (selectedLine?.type === 'handoff') return showSnackbar('PlayBuilder16'); // Handoff lines cannot be curved
        if (isDoubleBezier) {
            convertToSingleBezier();
        } else {
            convertToDoubleBezier();
        }
    };

    // it keeps content within the court boundaries
    const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

    // it gets the event coordinates from the mouse or touch event
    const getEventCoordinates = (e: MouseEvent | TouchEvent) => {
        const event = e instanceof MouseEvent ? e : e.touches[0] || e.changedTouches[0];
        return { x: event.clientX, y: event.clientY };
    };

    // it calculates the point on the bezier curve
    const bezierPoint = (t: number, fromX: number, fromY: number, controlX: number, controlY: number, toX: number, toY: number) => {
        const x = (1 - t) ** 2 * fromX + 2 * (1 - t) * t * controlX + t ** 2 * toX;
        const y = (1 - t) ** 2 * fromY + 2 * (1 - t) * t * controlY + t ** 2 * toY;
        return { x, y };
    };

    // it calculates the control point for the bezier curve
    const calculateControlPoint = (
        fromX: number,
        fromY: number,
        toX: number,
        toY: number,
        pointX: number,
        pointY: number,
        t: number = 0.5
    ) => {
        if (t === 0 || t === 1) return { x: fromX, y: fromY };
        const denom = 2 * (1 - t) * t;
        const controlX = (pointX - (1 - t) ** 2 * fromX - t ** 2 * toX) / denom;
        const controlY = (pointY - (1 - t) ** 2 * fromY - t ** 2 * toY) / denom;
        return { x: controlX, y: controlY };
    };

    // break down playdetails. Incomming props.playDetails is initial at start and 
    // then then gets the value from navigation/state/useLocation. Therefor, we have
    // to list props.playDetails as a dependency variable.
    useEffect(() => {
        const playDetailsLocal = props.playDetails;
        // Reset states for a new drill when playDetails is null
        if (!playDetailsLocal) {
            const initialFrame = operationMode === 'play'
                ? JSON.parse(JSON.stringify(FRAMEINIT))
                : JSON.parse(JSON.stringify(FRAMEINITEXERCISE));
            setFrames([initialFrame]);
            setCurrentFrameIndex(0);
            setDragging(DRAGGINGSTATE);
            setSelectedPlayerId(null);
            setSelectedLineId(null);
            setSelectedConeId(null);
            setSelectedTextBoxId(null);
            setSelectedShadeId(null);
            setCourtType('halfcourt');
            setNextConeId(1);
            setNextTextBoxId(1);
            setNextShadeId(1);
            setShowDescription(false);
            setIsModalOpen(false);
            setInitialPointerPosition(null);
            setIsDraggingLineEnd(false);
        } else {
            // Handle existing playDetails
            const correctedFrames = playDetailsLocal.frames.map((frame) => ({
                ...frame,
                description: frame.description || '',
                lines: frame.lines.map((line) => ({
                    ...line,
                    id: line.id || line.fromId,
                    color: line.color || '#000000',
                    controlX: line.controlX || (line.fromX + line.toX) / 2,
                    controlY: line.controlY || (line.fromY + line.toY) / 2,
                })),
                cones: frame.cones || [],
                texts: (frame as any).texts || [],
                shades: (frame as any).shades || [],
            }));
            const maxConeId = correctedFrames.reduce((max, frame) => {
                const frameMax = frame.cones.reduce((frameMax, cone) => Math.max(frameMax, cone.id), 0);
                return Math.max(max, frameMax);
            }, 0);

            const maxLineId = correctedFrames.reduce((max, frame) => {
                const frameMax = frame.lines.reduce((frameMax, line) => Math.max(frameMax, line.id), 0);
                return Math.max(max, frameMax);
            }, 0);
            setNextLineId(maxLineId + 1);

            const maxTextBoxId = correctedFrames.reduce((max, frame) => {
                const frameMax = (frame.texts || []).reduce((frameMax: number, tb: TextBox) => Math.max(frameMax, tb.id), 0);
                return Math.max(max, frameMax);
            }, 0);
            setNextTextBoxId(maxTextBoxId + 1);

            const maxShadeId = correctedFrames.reduce((max, frame) => {
                const frameMax = (frame.shades || []).reduce((frameMax: number, s: Shade) => Math.max(frameMax, s.id), 0);
                return Math.max(max, frameMax);
            }, 0);
            setNextShadeId(maxShadeId + 1);

            // Clean up frame IDs to ensure they are sequential and unique
            const cleanedFrames = cleanFrameIds(correctedFrames);
            const finalFrames = updateAllUniqueIds(cleanedFrames);

            setFrames(finalFrames);
            setCurrentFrameIndex(playDetailsLocal.currentFrameIndex || 0);
            setDragging(playDetailsLocal.dragging || DRAGGINGSTATE);
            setSelectedPlayerId(playDetailsLocal.selectedPlayerId || null);
            setSelectedLineId(playDetailsLocal.selectedLineId || null);
            setCourtType(playDetailsLocal.courtType || 'halfcourt');
            setNextConeId(maxConeId + 1);
            setSelectedConeId(null);
            setSelectedTextBoxId(null);
            setSelectedShadeId(null);
        }
    }, [props.playDetails, operationMode]);

    // Reset scale when switching orientation: 1.25 (middle of range 1.0-1.5) for portrait, 1.0 for landscape
    useEffect(() => {
        if (isPortrait) {
            setScale(1.25);
        } else {
            setScale(1.0);
        }
    }, [isPortrait]);

    const handleCourtChange = (_: React.MouseEvent<HTMLElement>, newType: 'halfcourt' | 'fullcourt' | null) => {
        if (newType) setCourtType(newType);
    };

    const handleSaveClick = () => {
        setIsSignInPromptOpen(true);
    };

    const handleDownloadPdf = async () => {
        setPdfUse3DPlayers(use3DPlayers);
        setIsPdfNameDialogOpen(true);
    };

    const handleConfirmDownloadPdf = async () => {
        try {
            const play = {
                attributes: {
                    title: pdfPlayName.trim() || props.title || 'Play',
                    field_play_description: pdfPlayName.trim() || 'Play',
                },
            };
            await generatePDFPlay({ uiSettings: { use3DPlayersPDF: pdfUse3DPlayers } }, play, {
                courtType,
                frames: frames.map((frame) => ({
                    ...frame,
                    texts: frame.texts || [],
                    shades: frame.shades || [],
                })),
            });
            setIsPdfNameDialogOpen(false);
        } catch (error) {
            showSnackbar('PlayBuilder04');
        }
    };

    const handleAddCone = (color: string) => {
        const newCone: Cone = {
            id: nextConeId,
            x: courtWidth / 2,
            y: courtHeight / 2,
            color,
        };
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.cones = [...(current.cones ?? []), newCone]; // Ensure cones is initialized
            updated[currentFrameIndex] = current;
            return updated;
        });
        setNextConeId(nextConeId + 1);
        setSelectedConeId(newCone.id);
        setSelectedPlayerId(null);
        setSelectedLineId(null);
        setSelectedTextBoxId(null);
    };

    const handleAddTextBox = () => {
        const DEFAULT_WIDTH = 180;
        const DEFAULT_HEIGHT = 80;
        let createdTextBoxId = 0;
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };

            const nextId = getMaxTextBoxId(updated) + 1;
            createdTextBoxId = nextId;

            const newTextBox: TextBox = {
                id: nextId,
                x: Math.round(clamp(courtWidth / 2 - DEFAULT_WIDTH / 2, 0, Math.max(0, courtWidth - DEFAULT_WIDTH))),
                y: Math.round(clamp(courtHeight / 2 - DEFAULT_HEIGHT / 2, 0, Math.max(0, courtHeight - DEFAULT_HEIGHT))),
                width: DEFAULT_WIDTH,
                height: DEFAULT_HEIGHT,
                text: '',
                fontSize: 14,
            };

            current.texts = [...(current.texts || []), newTextBox];
            updated[currentFrameIndex] = current;
            return updated;
        });
        setNextTextBoxId((prev) => Math.max(prev, createdTextBoxId + 1));
        setSelectedTextBoxId(createdTextBoxId);
        setSelectedPlayerId(null);
        setSelectedConeId(null);
        setSelectedLineId(null);
        setSelectedShadeId(null);
    };

    const handleAddShade = (shape: Shade['shape']) => {
        // Hardcoded light/transparent style will be applied in render.
        const DEFAULT_WIDTH = shape === 'circle' ? 110 : 220;
        const DEFAULT_HEIGHT = shape === 'circle' ? 110 : 140;
        let createdShadeId = 0;
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };

            const nextId = getMaxShadeId(updated) + 1;
            createdShadeId = nextId;

            const newShade: Shade = {
                id: nextId,
                shape,
                x: Math.round(clamp(courtWidth / 2 - DEFAULT_WIDTH / 2, 0, Math.max(0, courtWidth - DEFAULT_WIDTH))),
                y: Math.round(clamp(courtHeight / 2 - DEFAULT_HEIGHT / 2, 0, Math.max(0, courtHeight - DEFAULT_HEIGHT))),
                width: DEFAULT_WIDTH,
                height: DEFAULT_HEIGHT,
                color: '#38BDF8',
            };

            current.shades = [...(current.shades || []), newShade];
            updated[currentFrameIndex] = current;
            return updated;
        });
        setNextShadeId((prev) => Math.max(prev, createdShadeId + 1));
        setSelectedShadeId(createdShadeId);
        setSelectedPlayerId(null);
        setSelectedConeId(null);
        setSelectedLineId(null);
        setSelectedTextBoxId(null);
    };

    const handleDeleteSelectedShade = () => {
        if (selectedShadeId === null) return showSnackbar('PlayBuilderText02');
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.shades = (current.shades || []).filter((s) => s.id !== selectedShadeId);
            updated[currentFrameIndex] = current;
            // Keep nextShadeId consistent after deletions (even if user deletes highest id).
            setNextShadeId(getMaxShadeId(updated) + 1);
            return updated;
        });
        setSelectedShadeId(null);
    };

    const handleSetSelectedShadeColor = (color: string) => {
        if (selectedShadeId === null) return;
        const normalized = normalizeShadeColor(color);
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.shades = (current.shades || []).map((s) => (s.id === selectedShadeId ? { ...s, color: normalized } : s));
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleDeleteSelectedTextBox = () => {
        if (selectedTextBoxId === null) return showSnackbar('PlayBuilderText02');
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.texts = (current.texts || []).filter((tb) => tb.id !== selectedTextBoxId);
            updated[currentFrameIndex] = current;
            setNextTextBoxId(getMaxTextBoxId(updated) + 1);
            return updated;
        });
        setSelectedTextBoxId(null);
    };

    const handleSetSelectedConeColor = (color: string) => {
        if (selectedConeId === null) return;
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.cones = (current.cones || []).map((c) => (c.id === selectedConeId ? { ...c, color } : c));
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleSetSelectedTextBoxFontSize = (fontSize: number) => {
        if (selectedTextBoxId === null) return;
        const next = Math.max(8, Math.min(26, Math.round(fontSize)));
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.texts = (current.texts || []).map((tb) =>
                tb.id === selectedTextBoxId ? { ...tb, fontSize: next } : tb
            );
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newDescription = e.target.value;
        setFrames((prev) => {
            const updated = [...prev];
            updated[currentFrameIndex] = { ...updated[currentFrameIndex], description: newDescription };
            return updated;
        });
    };

    // it calculates where the next line starts from. for example, is !dashed, it will start from the end of the previous line
    // if it is dashed, it will start from the player
    const adjustLineConnections = (lines: Line[], updatedLine: Line, direction: 'forward' | 'backward', lineType?: Line['type']) => {
        const playerLines = getPlayerLinesInOrder(lines, updatedLine.fromId);
        const index = playerLines.findIndex((l) => l.id === updatedLine.id);
        if (direction === 'forward' && index < playerLines.length - 1) {
            const nextLine = playerLines[index + 1];
            if (lineType !== 'dashed' && lineType !== 'handoff') {
                nextLine.fromX = Math.round(updatedLine.toX); // No decimals
                nextLine.fromY = Math.round(updatedLine.toY); // No decimals
                nextLine.controlX = Math.round((nextLine.fromX + nextLine.toX) / 2);
                nextLine.controlY = Math.round((nextLine.fromY + nextLine.toY) / 2);
                adjustLineConnections(lines, nextLine, 'forward', nextLine.type);
            }
        } else if (direction === 'backward' && index > 0) {
            const prevLine = playerLines[index - 1];
            prevLine.toX = Math.round(updatedLine.fromX); // No decimals
            prevLine.toY = Math.round(updatedLine.fromY); // No decimals
            prevLine.controlX = Math.round((prevLine.fromX + prevLine.toX) / 2);
            prevLine.controlY = Math.round((prevLine.fromY + prevLine.toY) / 2);
            adjustLineConnections(lines, prevLine, 'backward', prevLine.type);
        }
    };

    const handleAddOffensivePlayer = (playerNumber: number) => {
        const newPlayer: Player = {
            id: Math.max(...currentFrame.players.map((p) => p.id), 0) + 1,
            x: courtWidth / 2,
            y: courtHeight / 2,
            number: playerNumber, // 1-6 for players, 0 for coach (ignored in display)
            hasBall: false,
            isDefender: false,
            isCoach: playerNumber === 0, // Mark as coach if number is 0
            color: '#000000',
        };

        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.players = [...current.players, newPlayer];
            updated[currentFrameIndex] = current;
            return updated;
        });

        setSelectedPlayerId(newPlayer.id);
        setSelectedConeId(null);
        setSelectedLineId(null);
        setSelectedTextBoxId(null);
    };

    const handleAddDefender = (defenderNumber: number) => {
        const newDefender: Player = {
            id: Math.max(...currentFrame.players.map((p) => p.id), 0) + 1,
            x: courtWidth / 2, // Place in the center initially
            y: courtHeight / 2,
            number: defenderNumber,
            hasBall: false,
            isDefender: true,
            color: 'red',
        };

        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.players = [...current.players, newDefender];
            updated[currentFrameIndex] = current;
            return updated;
        });

        setSelectedPlayerId(newDefender.id);
        setSelectedConeId(null);
        setSelectedLineId(null);
        setSelectedTextBoxId(null);
    };

    const handleAddSpecialDefender = (defenderNumber: number) => {
        const newSpecialDefender: Player = {
            id: Math.max(...currentFrame.players.map((p) => p.id), 0) + 1,
            x: courtWidth / 2,
            y: courtHeight / 2,
            number: defenderNumber,
            hasBall: false,
            isDefender: true,
            isSpecialDefender: true,
            color: 'red',
            armRotation: 0, // Default rotation (facing up)
        };

        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.players = [...current.players, newSpecialDefender];
            updated[currentFrameIndex] = current;
            return updated;
        });

        setSelectedPlayerId(newSpecialDefender.id);
        setSelectedConeId(null);
        setSelectedLineId(null);
        setSelectedTextBoxId(null);
    };

    const handleDeletePlayer = () => {
        if (selectedPlayerId === null) return showSnackbar('PlayBuilder05');

        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.players = current.players.filter((p) => p.id !== selectedPlayerId);
            current.lines = current.lines.filter(
                (l) => l.fromId !== selectedPlayerId && l.toId !== selectedPlayerId
            );
            updated[currentFrameIndex] = current;
            return updated;
        });

        setSelectedPlayerId(null);
        setSelectedLineId(null);
    };

    // New function to handle cone deletion
    const handleDeleteCone = () => {
        if (selectedConeId === null) return showSnackbar('PlayBuilder15'); // Add a translation key like "Select a cone to delete"
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.cones = current.cones.filter((c) => c.id !== selectedConeId);
            updated[currentFrameIndex] = current;
            return updated;
        });
        setSelectedConeId(null); // Clear selection after deletion
    };

    // it handles the move event
    const handleMove = (e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>) => {
        if (!dragging.type || dragging.id === null || !svgRef.current) {
            // console.log('handleMove blocked: ', { type: dragging.type, id: dragging.id, svgRef: svgRef.current });
            return;
        }

        // Get mouse/touch coordinates relative to SVG
        const { x: clientX, y: clientY } = getEventCoordinates(e.nativeEvent);
        const pt = svgRef.current.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const cursor = pt.matrixTransform(svgRef.current.getScreenCTM()?.inverse());
        const mouseX = Math.round(clamp(cursor.x, 0, courtWidth));
        const mouseY = Math.round(clamp(cursor.y, 0, courtHeight));

        // Drag threshold check (for text/shade we want "click+drag" to move immediately)
        const DRAG_THRESHOLD = (dragging.type === 'text' || dragging.type === 'textResize' || dragging.type === 'shade' || dragging.type === 'shadeResize') ? 0 : 5;
        if (!dragging.isDragging) {
            if (initialPointerPosition) {
                const distance = Math.hypot(mouseX - initialPointerPosition.x, mouseY - initialPointerPosition.y);
                // console.log(`Distance check: ${distance}`);
                if (distance > DRAG_THRESHOLD) {
                    setDragging((prev) => ({ ...prev, isDragging: true }));
                    // If user is dragging, don't auto-focus the textarea on mouse up
                    pendingTextFocusIdRef.current = null;
                }
                else return;
            } else {
                // console.log('No initial position set');
                return;
            }
        }

        // console.log(`handleMove: type=${dragging.type}, id=${dragging.id}, x=${mouseX}, y=${mouseY}`);

        // Define basket points
        const basketPoints = courtType === 'halfcourt'
            ? [{ x: 270, y: 85 }] // Half court: single basket
            : [
                { x: 80, y: 274 },  // Full court: left basket
                { x: 830, y: 274 }  // Full court: right basket
            ];

        // Update frames based on dragging type
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };

            switch (dragging.type) {
                case 'player': {
                    // Move player and adjust connected lines
                    current.players = current.players.map((p) =>
                        p.id === dragging.id ? { ...p, x: mouseX, y: mouseY } : p
                    );
                    const playerLines = getPlayerLinesInOrder(current.lines, dragging.id!);
                    current.lines = current.lines.map((line) => {
                        const index = playerLines.findIndex((l) => l.id === line.id);
                        if (index === 0) {
                            // First line from player - update from position and control point
                            return {
                                ...line,
                                fromX: mouseX,
                                fromY: mouseY,
                                controlX: line.controlModified ? line.controlX : Math.round((mouseX + line.toX) / 2),
                                controlY: line.controlModified ? line.controlY : Math.round((mouseY + line.toY) / 2),
                            };
                        }
                        if (index > 0 && playerLines[index - 1].type === 'dashed') {
                            // Line after a dashed line - update from position and control point
                            return {
                                ...line,
                                fromX: mouseX,
                                fromY: mouseY,
                                controlX: line.controlModified ? line.controlX : Math.round((mouseX + line.toX) / 2),
                                controlY: line.controlModified ? line.controlY : Math.round((mouseY + line.toY) / 2),
                            };
                        }
                        if (line.type === 'dashed' && line.toId === dragging.id) {
                            // Dashed line ending at this player - update to position and control point
                            return {
                                ...line,
                                toX: mouseX,
                                toY: mouseY,
                                controlX: line.controlModified ? line.controlX : Math.round((line.fromX + mouseX) / 2),
                                controlY: line.controlModified ? line.controlY : Math.round((line.fromY + mouseY) / 2),
                            };
                        }
                        if (line.type === 'handoff' && line.toId === dragging.id) {
                            // Handoff line ending at this player - update to position and control point
                            return {
                                ...line,
                                toX: mouseX,
                                toY: mouseY,
                                controlX: line.controlModified ? line.controlX : Math.round((line.fromX + mouseX) / 2),
                                controlY: line.controlModified ? line.controlY : Math.round((line.fromY + mouseY) / 2),
                            };
                        }
                        return line;
                    });
                    break;
                }

                case 'end': {
                    // Move line endpoint with snapping logic
                    current.lines = current.lines.map((line) => {
                        if (line.id !== dragging.id) return line;

                        let toX = mouseX;
                        let toY = mouseY;
                        const SNAP_DISTANCE = 20; // Distance threshold for snapping

                        if (line.type === 'shot') {
                            // Check distance to both baskets and snap to the closer one
                            let closestBasket = basketPoints[0];
                            let minDist = Math.hypot(mouseX - closestBasket.x, mouseY - closestBasket.y);

                            for (const basket of basketPoints) {
                                const dist = Math.hypot(mouseX - basket.x, mouseY - basket.y);
                                if (dist < minDist) {
                                    minDist = dist;
                                    closestBasket = basket;
                                }
                            }

                            if (minDist <= SNAP_DISTANCE) {
                                toX = closestBasket.x; // Snap to the closest basket
                                toY = closestBasket.y;
                            }
                        } else if (line.type === 'dashed' || line.type === 'handoff') {
                            // Existing snapping logic for dashed and handoff lines
                            const otherLines = current.lines.filter((l) => l.fromId !== line.fromId && ['straight', 'bar', 'zigzag'].includes(l.type));
                            for (const otherLine of otherLines) {
                                const dist = Math.hypot(mouseX - otherLine.toX, mouseY - otherLine.toY);
                                if (dist <= SNAP_DISTANCE) {
                                    toX = Math.round(otherLine.toX);
                                    toY = Math.round(otherLine.toY);
                                    const updatedLine = {
                                        ...line,
                                        toX,
                                        toY,
                                        toId: otherLine.fromId,
                                        controlX: line.controlModified ? line.controlX : Math.round((line.fromX + toX) / 2),
                                        controlY: line.controlModified ? line.controlY : Math.round((line.fromY + toY) / 2),
                                    };
                                    adjustLineConnections(current.lines, updatedLine, 'forward', line.type);
                                    return updatedLine;
                                }
                            }
                            const nearbyPlayer = findNearbyPlayer(mouseX, mouseY);
                            if (nearbyPlayer !== null && nearbyPlayer !== line.fromId) {
                                const target = current.players.find((p) => p.id === nearbyPlayer);
                                if (target) {
                                    toX = Math.round(target.x);
                                    toY = Math.round(target.y);
                                    const updatedLine = {
                                        ...line,
                                        toX,
                                        toY,
                                        toId: target.id,
                                        controlX: line.controlModified ? line.controlX : Math.round((line.fromX + toX) / 2),
                                        controlY: line.controlModified ? line.controlY : Math.round((line.fromY + toY) / 2),
                                    };
                                    adjustLineConnections(current.lines, updatedLine, 'forward', line.type);
                                    return updatedLine;
                                }
                            }
                        }

                        // Update line endpoint and control points
                        const isDuplicated = line.midX !== undefined && line.midY !== undefined && line.controlX2 !== undefined && line.controlY2 !== undefined;
                        const updatedLine = {
                            ...line,
                            toX,
                            toY,
                            ...(isDuplicated
                                ? {
                                    controlX2: line.control2Modified ? line.controlX2 : Math.round((line.midX! + toX) / 2),
                                    controlY2: line.control2Modified ? line.controlY2 : Math.round((line.midY! + toY) / 2),
                                }
                                : {
                                    controlX: line.controlModified ? line.controlX : Math.round((line.fromX + toX) / 2),
                                    controlY: line.controlModified ? line.controlY : Math.round((line.fromY + toY) / 2),
                                }),
                            toId: (line.type === 'dashed' || line.type === 'handoff') && toX === mouseX && toY === mouseY ? undefined : line.toId,
                        };
                        adjustLineConnections(current.lines, updatedLine, 'forward', line.type);
                        return updatedLine;
                    });
                    break;
                }

                case 'control': {
                    // Move first control point
                    current.lines = current.lines.map((line) => {
                        if (line.id === dragging.id) {
                            const newControl = calculateControlPoint(
                                line.fromX,
                                line.fromY,
                                line.midX ?? line.toX,
                                line.midY ?? line.toY,
                                mouseX,
                                mouseY,
                                0.5
                            );
                            return {
                                ...line,
                                controlX: Math.round(newControl.x),
                                controlY: Math.round(newControl.y),
                                controlModified: true,
                            };
                        }
                        return line;
                    });
                    break;
                }

                case 'control2': {
                    // Move second control point (double Bezier)
                    current.lines = current.lines.map((line) => {
                        if (line.id === dragging.id && line.midX && line.midY && line.controlX2 && line.controlY2) {
                            const newControl = calculateControlPoint(
                                line.midX,
                                line.midY,
                                line.toX,
                                line.toY,
                                mouseX,
                                mouseY,
                                0.5
                            );
                            return {
                                ...line,
                                controlX2: Math.round(newControl.x),
                                controlY2: Math.round(newControl.y),
                                control2Modified: true,
                            };
                        }
                        return line;
                    });
                    break;
                }

                case 'mid': {
                    // Move midpoint (double Bezier)
                    current.lines = current.lines.map((line) => {
                        if (line.id === dragging.id && line.midX && line.midY) {
                            return {
                                ...line,
                                midX: mouseX,
                                midY: mouseY,
                                controlX: line.controlModified ? line.controlX : Math.round((line.fromX + mouseX) / 2),
                                controlY: line.controlModified ? line.controlY : Math.round((line.fromY + mouseY) / 2),
                                controlX2: line.control2Modified ? line.controlX2 : Math.round((mouseX + line.toX) / 2),
                                controlY2: line.control2Modified ? line.controlY2 : Math.round((mouseY + line.toY) / 2),
                            };
                        }
                        return line;
                    });
                    break;
                }

                case 'cone': {
                    // Move cone
                    current.cones = current.cones.map((cone) =>
                        cone.id === dragging.id ? { ...cone, x: mouseX, y: mouseY } : cone
                    );
                    break;
                }

                case 'text': {
                    // Move text box
                    const tb = (current.texts || []).find((tbox) => tbox.id === dragging.id);
                    if (tb && textDragOffset) {
                        const newX = Math.round(clamp(mouseX - textDragOffset.dx, 0, Math.max(0, courtWidth - tb.width)));
                        const newY = Math.round(clamp(mouseY - textDragOffset.dy, 0, Math.max(0, courtHeight - tb.height)));
                        current.texts = (current.texts || []).map((tbox) =>
                            tbox.id === dragging.id ? { ...tbox, x: newX, y: newY } : tbox
                        );
                    }
                    break;
                }

                case 'textResize': {
                    // Resize text box (bottom-right corner)
                    const tb = (current.texts || []).find((tbox) => tbox.id === dragging.id);
                    if (tb && textResizeStart && textResizeCorner) {
                        const MIN_W = 60;
                        const MIN_H = 40;
                        const dx = mouseX - textResizeStart.startX;
                        const dy = mouseY - textResizeStart.startY;

                        let newX = textResizeStart.x;
                        let newY = textResizeStart.y;
                        let newW = textResizeStart.width;
                        let newH = textResizeStart.height;

                        if (textResizeCorner === 'br') {
                            newW = textResizeStart.width + dx;
                            newH = textResizeStart.height + dy;
                        } else if (textResizeCorner === 'tr') {
                            newW = textResizeStart.width + dx;
                            newH = textResizeStart.height - dy;
                            newY = textResizeStart.y + dy;
                        } else if (textResizeCorner === 'bl') {
                            newW = textResizeStart.width - dx;
                            newH = textResizeStart.height + dy;
                            newX = textResizeStart.x + dx;
                        } else if (textResizeCorner === 'tl') {
                            newW = textResizeStart.width - dx;
                            newH = textResizeStart.height - dy;
                            newX = textResizeStart.x + dx;
                            newY = textResizeStart.y + dy;
                        }

                        // Clamp position/size inside court
                        newW = Math.round(clamp(newW, MIN_W, courtWidth));
                        newH = Math.round(clamp(newH, MIN_H, courtHeight));
                        newX = Math.round(clamp(newX, 0, Math.max(0, courtWidth - newW)));
                        newY = Math.round(clamp(newY, 0, Math.max(0, courtHeight - newH)));

                        current.texts = (current.texts || []).map((tbox) =>
                            tbox.id === dragging.id ? { ...tbox, x: newX, y: newY, width: newW, height: newH } : tbox
                        );
                    }
                    break;
                }

                case 'shade': {
                    const sh = (current.shades || []).find((s) => s.id === dragging.id);
                    if (sh && shadeDragOffset) {
                        const newX = Math.round(clamp(mouseX - shadeDragOffset.dx, 0, Math.max(0, courtWidth - sh.width)));
                        const newY = Math.round(clamp(mouseY - shadeDragOffset.dy, 0, Math.max(0, courtHeight - sh.height)));
                        current.shades = (current.shades || []).map((s) => (s.id === dragging.id ? { ...s, x: newX, y: newY } : s));
                    }
                    break;
                }

                case 'shadeResize': {
                    const sh = (current.shades || []).find((s) => s.id === dragging.id);
                    if (sh && shadeResizeStart && shadeResizeCorner) {
                        // Allow non-uniform resizing for BOTH rect and "circle" shades.
                        // "circle" is rendered as an ellipse (rx/ry) so users can stretch it.
                        const MIN_W = 60;
                        const MIN_H = 60;
                        const dx = mouseX - shadeResizeStart.startX;
                        const dy = mouseY - shadeResizeStart.startY;

                        let newX = shadeResizeStart.x;
                        let newY = shadeResizeStart.y;
                        let newW = shadeResizeStart.width;
                        let newH = shadeResizeStart.height;

                        if (shadeResizeCorner === 'br') {
                            newW = shadeResizeStart.width + dx;
                            newH = shadeResizeStart.height + dy;
                        } else if (shadeResizeCorner === 'tr') {
                            newW = shadeResizeStart.width + dx;
                            newH = shadeResizeStart.height - dy;
                            newY = shadeResizeStart.y + dy;
                        } else if (shadeResizeCorner === 'bl') {
                            newW = shadeResizeStart.width - dx;
                            newH = shadeResizeStart.height + dy;
                            newX = shadeResizeStart.x + dx;
                        } else if (shadeResizeCorner === 'tl') {
                            newW = shadeResizeStart.width - dx;
                            newH = shadeResizeStart.height - dy;
                            newX = shadeResizeStart.x + dx;
                            newY = shadeResizeStart.y + dy;
                        }

                        newW = Math.round(clamp(newW, MIN_W, courtWidth));
                        newH = Math.round(clamp(newH, MIN_H, courtHeight));
                        newX = Math.round(clamp(newX, 0, Math.max(0, courtWidth - newW)));
                        newY = Math.round(clamp(newY, 0, Math.max(0, courtHeight - newH)));

                        current.shades = (current.shades || []).map((s) =>
                            s.id === dragging.id ? { ...s, x: newX, y: newY, width: newW, height: newH } : s
                        );
                    }
                    break;
                }

                case 'armRotation': {
                    // Rotate arms around the player
                    const player = current.players.find((p) => p.id === dragging.id);
                    if (player && player.isSpecialDefender) {
                        // Calculate angle from player center to mouse position
                        const dx = mouseX - player.x;
                        const dy = mouseY - player.y;
                        // Calculate angle in degrees (0-360, where 0 is up/north)
                        let angle = Math.atan2(dy, dx) * (180 / Math.PI);
                        // Convert to 0-360 range (at-an2 gives -180 to 180, we want 0-360)
                        angle = (angle + 90 + 360) % 360; // Add 90 to make 0 point up, then normalize

                        current.players = current.players.map((p) =>
                            p.id === dragging.id ? { ...p, armRotation: angle } : p
                        );
                    }
                    break;
                }

                default:
                // console.log(`Unhandled dragging type: ${dragging.type}`);
            }

            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleStart = (
        e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>,
        type: 'player' | 'end' | 'control' | 'control2' | 'mid' | 'cone' | 'armRotation' | 'text' | 'textResize' | 'shade' | 'shadeResize',
        id: number
    ) => {
        e.stopPropagation();
        const { x, y } = getEventCoordinates(e.nativeEvent);
        if (!svgRef.current) return;
        const pt = svgRef.current.createSVGPoint();
        pt.x = x;
        pt.y = y;
        const cursor = pt.matrixTransform(svgRef.current.getScreenCTM()?.inverse());
        setInitialPointerPosition({ x: clamp(cursor.x, 0, courtWidth), y: clamp(cursor.y, 0, courtHeight) });

        // Only deselect line if dragging a player or cone
        if (type === 'player' || type === 'cone' || type === 'text' || type === 'shade') {
            setSelectedLineId(null);
        }
        // Don't deselect player when rotating arms
        if (type !== 'armRotation') {
            // Always deselect players and cones when starting a drag (except arm rotation)
            setSelectedPlayerId(null);
            setSelectedConeId(null);
        }
        if (type !== 'armRotation') {
            setSelectedTextBoxId(null);
            setSelectedShadeId(null);
        }

        // Set selection based on type
        if (type === 'player') {
            setSelectedPlayerId(id);
        } else if (type === 'cone') {
            setSelectedConeId(id);
        } else if (type === 'armRotation') {
            // Keep player selected when rotating arms
            setSelectedPlayerId(id);
        } else if (type === 'text' || type === 'textResize') {
            setSelectedTextBoxId(id);
            const tb = (currentFrame.texts || []).find((tbox) => tbox.id === id);
            if (tb) {
                const cx = clamp(cursor.x, 0, courtWidth);
                const cy = clamp(cursor.y, 0, courtHeight);
                if (type === 'text') {
                    setTextDragOffset({ dx: cx - tb.x, dy: cy - tb.y });
                    setTextResizeStart(null);
                    setTextResizeCorner(null);
                    pendingTextFocusIdRef.current = id;
                } else {
                    setTextDragOffset(null);
                    setTextResizeStart({ startX: cx, startY: cy, x: tb.x, y: tb.y, width: tb.width, height: tb.height });
                    pendingTextFocusIdRef.current = null;
                }
            }
        }
        else if (type === 'shade' || type === 'shadeResize') {
            setSelectedShadeId(id);
            const sh = (currentFrame.shades || []).find((s) => s.id === id);
            if (sh) {
                const cx = clamp(cursor.x, 0, courtWidth);
                const cy = clamp(cursor.y, 0, courtHeight);
                if (type === 'shade') {
                    setShadeDragOffset({ dx: cx - sh.x, dy: cy - sh.y });
                    setShadeResizeStart(null);
                    setShadeResizeCorner(null);
                } else {
                    setShadeDragOffset(null);
                    setShadeResizeStart({ startX: cx, startY: cy, x: sh.x, y: sh.y, width: sh.width, height: sh.height });
                }
            }
        }
        // For line-related types ('end', 'control', 'control2', 'mid'), preserve selectedLineId

        setDragging({ isDragging: false, type, id });

        if (type === 'end' && currentFrame.lines.find((line) => line.id === id)?.type === 'dashed') {
            setIsDraggingLineEnd(true);
        }
        if (type === 'end' && currentFrame.lines.find((line) => line.id === id)?.type === 'handoff') {
            setIsDraggingLineEnd(true);
        }
    };

    const handleEnd = () => {
        const focusId = pendingTextFocusIdRef.current;
        setDragging({ isDragging: false, type: null, id: null });
        setInitialPointerPosition(null);
        setIsDraggingLineEnd(false);
        setTextDragOffset(null);
        setTextResizeStart(null);
        setTextResizeCorner(null);
        setShadeDragOffset(null);
        setShadeResizeStart(null);
        setShadeResizeCorner(null);
        pendingTextFocusIdRef.current = null;
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            if (dragging.type === 'end' && current.lines) {
                current.lines = current.lines.map((line) => {
                    if (line.id === dragging.id && (line.type === 'dashed' || line.type === 'handoff')) {
                        const nearbyPlayer = findNearbyPlayer(line.toX, line.toY);
                        if (nearbyPlayer !== null && nearbyPlayer !== line.fromId) {
                            const target = current.players.find((p) => p.id === nearbyPlayer);
                            if (target) {
                                const updatedLine = {
                                    ...line,
                                    toX: target.x,
                                    toY: target.y,
                                    toId: target.id,
                                    controlX: (line.fromX + target.x) / 2,
                                    controlY: (line.fromY + target.y) / 2,
                                };
                                adjustLineConnections(current.lines, updatedLine, 'forward', line.type);
                                setSelectedPlayerId(target.id);
                                setSelectedLineId(null);
                                return updatedLine;
                            }
                        }
                    }
                    return line;
                });
            }
            updated[currentFrameIndex] = current;
            return updated;
        });

        // Focus textarea only if it was a "click" (not a drag)
        if (focusId !== null) {
            // Defer to allow state to settle
            setTimeout(() => {
                const el = textAreaRefs.current[focusId];
                if (el) el.focus();
            }, 0);
        }
    };

    const handleDragOver = (e: React.DragEvent<SVGElement>) => {
        e.preventDefault(); // Necessary to allow dropping
    };

    const handleDrop = (e: React.DragEvent<SVGElement>) => {
        e.preventDefault();
        if (!svgRef.current) return;

        const type = e.dataTransfer.getData('type');
        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const cursor = pt.matrixTransform(svgRef.current.getScreenCTM()?.inverse());
        const x = clamp(Math.round(cursor.x), 0, courtWidth);
        const y = clamp(Math.round(cursor.y), 0, courtHeight);

        if (type === 'offensive') {
            const number = parseInt(e.dataTransfer.getData('number'), 10);
            const newPlayer: Player = {
                id: Math.max(...currentFrame.players.map((p) => p.id), 0) + 1,
                x,
                y,
                number,
                hasBall: false,
                isDefender: false,
                isCoach: number === 0,
            };
            setFrames((prev) => {
                const updated = [...prev];
                updated[currentFrameIndex].players.push(newPlayer);
                return updated;
            });
            setSelectedPlayerId(newPlayer.id);
            setSelectedConeId(null);
            setSelectedLineId(null);
            setSelectedTextBoxId(null);
        } else if (type === 'defender') {
            const number = parseInt(e.dataTransfer.getData('number'), 10);
            const newDefender: Player = {
                id: Math.max(...currentFrame.players.map((p) => p.id), 0) + 1,
                x,
                y,
                number,
                hasBall: false,
                isDefender: true,
            };
            setFrames((prev) => {
                const updated = [...prev];
                updated[currentFrameIndex].players.push(newDefender);
                return updated;
            });
            setSelectedPlayerId(newDefender.id);
            setSelectedConeId(null);
            setSelectedLineId(null);
            setSelectedTextBoxId(null);
        } else if (type === 'specialDefender') {
            const number = parseInt(e.dataTransfer.getData('number'), 10);
            const newSpecialDefender: Player = {
                id: Math.max(...currentFrame.players.map((p) => p.id), 0) + 1,
                x,
                y,
                number,
                hasBall: false,
                isDefender: true,
                isSpecialDefender: true,
                color: 'red',
                armRotation: 0, // Default rotation (facing up)
            };
            setFrames((prev) => {
                const updated = [...prev];
                updated[currentFrameIndex].players.push(newSpecialDefender);
                return updated;
            });
            setSelectedPlayerId(newSpecialDefender.id);
            setSelectedConeId(null);
            setSelectedLineId(null);
            setSelectedTextBoxId(null);
        } else if (type === 'cone') {
            const color = e.dataTransfer.getData('color');
            const newCone: Cone = {
                id: nextConeId,
                x,
                y,
                color,
            };
            setFrames((prev) => {
                const updated = [...prev];
                updated[currentFrameIndex].cones.push(newCone);
                return updated;
            });
            setNextConeId(nextConeId + 1);
            setSelectedConeId(newCone.id);
            setSelectedPlayerId(null);
            setSelectedLineId(null);
            setSelectedTextBoxId(null);
            setSelectedShadeId(null);
        } else if (type === 'textBox') {
            const DEFAULT_WIDTH = 180;
            const DEFAULT_HEIGHT = 80;
            const newTextBox: TextBox = {
                id: nextTextBoxId,
                x: Math.round(clamp(x - DEFAULT_WIDTH / 2, 0, Math.max(0, courtWidth - DEFAULT_WIDTH))),
                y: Math.round(clamp(y - DEFAULT_HEIGHT / 2, 0, Math.max(0, courtHeight - DEFAULT_HEIGHT))),
                width: DEFAULT_WIDTH,
                height: DEFAULT_HEIGHT,
                text: '',
                fontSize: 14,
            };
            setFrames((prev) => {
                const updated = [...prev];
                const current = { ...updated[currentFrameIndex] };
                current.texts = [...(current.texts || []), newTextBox];
                updated[currentFrameIndex] = current;
                return updated;
            });
            setNextTextBoxId(nextTextBoxId + 1);
            setSelectedTextBoxId(newTextBox.id);
            setSelectedPlayerId(null);
            setSelectedConeId(null);
            setSelectedLineId(null);
            setSelectedShadeId(null);
        } else if (type === 'shadeCircle' || type === 'shadeRect') {
            const isCircle = type === 'shadeCircle';
            const DEFAULT_W = isCircle ? 110 : 220;
            const DEFAULT_H = isCircle ? 110 : 140;
            let createdShadeId = 0;
            setFrames((prev) => {
                const updated = [...prev];
                const current = { ...updated[currentFrameIndex] };

                const nextId = getMaxShadeId(updated) + 1;
                createdShadeId = nextId;

                const newShade: Shade = {
                    id: nextId,
                    shape: isCircle ? 'circle' : 'rect',
                    x: Math.round(clamp(x - DEFAULT_W / 2, 0, Math.max(0, courtWidth - DEFAULT_W))),
                    y: Math.round(clamp(y - DEFAULT_H / 2, 0, Math.max(0, courtHeight - DEFAULT_H))),
                    width: DEFAULT_W,
                    height: DEFAULT_H,
                    color: '#38BDF8',
                };

                current.shades = [...(current.shades || []), newShade];
                updated[currentFrameIndex] = current;
                return updated;
            });
            setNextShadeId((prev) => Math.max(prev, createdShadeId + 1));
            setSelectedShadeId(createdShadeId);
            setSelectedPlayerId(null);
            setSelectedConeId(null);
            setSelectedLineId(null);
            setSelectedTextBoxId(null);
        }
    };

    const handleLineClick = (lineId: number) => {
        const selectedLine = currentFrame.lines.find((line) => line.id === lineId);
        if (selectedLine) {
            setSelectedLineId(lineId);
            setSelectedPlayerId(null); // Deselect player
        }
        setSelectedConeId(null);
        setSelectedTextBoxId(null);
        setSelectedShadeId(null);
    };

    const handlePlayerClick = (playerId: number) => {
        setSelectedPlayerId(playerId);
        setSelectedLineId(null);
        setSelectedConeId(null);
        setSelectedTextBoxId(null);
        setSelectedShadeId(null);
    };

    const handleConeClick = (coneId: number) => {
        setSelectedConeId(coneId);
        setSelectedPlayerId(null);
        setSelectedLineId(null);
        setSelectedTextBoxId(null);
        setSelectedShadeId(null);
    };

    const handleTextBoxClick = (textBoxId: number) => {
        setSelectedTextBoxId(textBoxId);
        setSelectedPlayerId(null);
        setSelectedLineId(null);
        setSelectedConeId(null);
        setSelectedShadeId(null);
    };


    // it finds the nearest player to the clicked point so we can connect the line to it
    const findNearbyPlayer = (x: number, y: number): number | null => {
        const proximity = 20;
        const current = frames[currentFrameIndex];
        let closest: number | null = null, minDist = proximity;
        current.players.forEach((p) => {
            const d = Math.hypot(p.x - x, p.y - y);
            if (d <= minDist) {
                minDist = d;
                closest = p.id;
            }
        });
        return closest;
    };

    const getNextLineOrder = (lines: Line[]) => (lines.length ? Math.max(...lines.map((l) => l.order ?? 1)) + 1 : 1);

    const getLineOrigin = (playerId: number, lines: Line[], players: Player[]) => {
        const playerLines = getPlayerLinesInOrder(lines, playerId);
        if (playerLines.length === 0) {
            const player = players.find((p) => p.id === playerId);
            return player ? { x: Math.round(player.x), y: Math.round(player.y) } : null;
        }

        // Find the last non-dashed, non-shot, non-handoff line to use its endpoint as the origin
        const lastNonDashedShotHandoffLine = playerLines
            .filter((line) => line.type !== 'dashed' && line.type !== 'shot' && line.type !== 'handoff')
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .pop();

        if (lastNonDashedShotHandoffLine) {
            return { x: Math.round(lastNonDashedShotHandoffLine.toX), y: Math.round(lastNonDashedShotHandoffLine.toY) };
        }

        // If no non-dashed, non-shot, non-handoff lines exist, use the player's current position
        const player = players.find((p) => p.id === playerId);
        return player ? { x: Math.round(player.x), y: Math.round(player.y) } : null;
    };

    const calculateEndPoint = (origin: { x: number; y: number }, playerLines: Line[]) => {
        const LINE_LENGTH = 125;
        const centerX = courtWidth / 2, centerY = courtHeight / 2;
        if (playerLines.length === 0 || playerLines[0].type === 'dashed' || playerLines[0].type === 'handoff') {
            const dx = centerX - origin.x, dy = centerY - origin.y, magnitude = Math.hypot(dx, dy) || 1;
            return {
                x: clamp(origin.x + (dx / magnitude) * LINE_LENGTH, 0, courtWidth),
                y: clamp(origin.y + (dy / magnitude) * LINE_LENGTH, 0, courtHeight),
            };
        }
        const firstLine = playerLines[0];
        const dxFirst = firstLine.toX - firstLine.fromX, dyFirst = firstLine.toY - firstLine.fromY;
        const magnitudeFirst = Math.hypot(dxFirst, dyFirst) || 1;
        const dxRotated = (dyFirst / magnitudeFirst) * LINE_LENGTH, dyRotated = (-dxFirst / magnitudeFirst) * LINE_LENGTH;
        return {
            x: clamp(origin.x + dxRotated, 0, courtWidth),
            y: clamp(origin.y + dyRotated, 0, courtHeight),
        };
    };

    const createLine = (type: 'straight' | 'zigzag' | 'dashed' | 'bar' | 'shot' | 'handoff') => {
        // Determine the player to draw the line from
        let playerId: number | null = null;
        if (selectedLineId !== null) {
            const selectedLine = currentFrame.lines.find((line) => line.id === selectedLineId);
            playerId = selectedLine?.fromId ?? null;
        } else if (selectedPlayerId !== null) {
            playerId = selectedPlayerId;
        }

        if (playerId === null) {
            showSnackbar('PlayBuilder00'); // "Select a player or line first"
            return;
        }

        const current = frames[currentFrameIndex];
        const selectedPlayer = current.players.find((p) => p.id === playerId);
        if (!selectedPlayer) return;

        // Get player's lines and check ball possession
        const playerLines = getPlayerLinesInOrder(current.lines, playerId);
        const orderedPassShotHandoffLines = current.lines
            .filter((line) => (line.type === 'dashed' || line.type === 'shot' || line.type === 'handoff') && (line.fromId === playerId || line.toId === playerId))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        let hasBall = selectedPlayer.hasBall;
        for (const line of orderedPassShotHandoffLines) {
            if (line.fromId === playerId && (line.type === 'dashed' || line.type === 'shot' || line.type === 'handoff')) hasBall = false; // Player passes, shoots, or handoffs
            else if (line.toId === playerId && (line.type === 'dashed' || line.type === 'handoff')) hasBall = true; // Player receives pass or handoff
        }

        // Validate action based on ball possession
        const canDribble = hasBall && (type === 'straight' || type === 'bar' || type === 'zigzag');
        const canPass = hasBall && (type === 'dashed' || type === 'handoff');
        const canShoot = hasBall && type === 'shot';
        const canOnlyMoveOrScreen = !hasBall && (type === 'straight' || type === 'bar'); // Allow bar after shot

        if (hasBall && !canDribble && !canPass && !canShoot) {
            showSnackbar('PlayBuilder01'); // "Player with ball can only dribble, pass, or shoot"
            return;
        }
        if (!hasBall && !canOnlyMoveOrScreen) {
            showSnackbar('PlayBuilder01'); // "Player without ball can only move or screen"
            return;
        }

        // Determine starting point based on last non-dashed, non-shot line
        const origin = getLineOrigin(playerId, current.lines, current.players);
        if (!origin) return;

        // Define basket points
        const basketPoints = courtType === 'halfcourt'
            ? [{ x: 270, y: 85 }] // Half court: single basket
            : [
                { x: 80, y: 274 },  // Full court: left basket
                { x: 830, y: 274 }  // Full court: right basket
            ];

        // Determine endpoint
        let toX: number, toY: number;
        if (type === 'shot') {
            if (courtType === 'halfcourt') {
                toX = basketPoints[0].x; // Center of the hoop in half court
                toY = basketPoints[0].y;
            } else {
                // Full court: choose basket based on player's starting position
                const targetBasket = origin.x < courtWidth / 2 ? basketPoints[0] : basketPoints[1];
                toX = targetBasket.x;
                toY = targetBasket.y;
            }
        } else {
            const { x: endX, y: endY } = calculateEndPoint(origin, playerLines);
            toX = endX;
            toY = endY;
        }

        // Generate new line ID based only on current frame's existing lines
        const maxLineIdInCurrentFrame = current.lines.length > 0 ? Math.max(...current.lines.map(l => l.id)) : 0;
        const newLineId = maxLineIdInCurrentFrame + 1;

        // Determine the color for the new line
        // Priority: 1. Last line's color, 2. Player's color, 3. Default color
        let lineColor: string;
        let handoffLineColor: string;

        if (playerLines.length > 0) {
            // Use the color of the last line created by this player
            const lastLine = playerLines[playerLines.length - 1];
            // If last line was handoff, use handoffColor instead of the white color
            lineColor = lastLine.type === 'handoff'
                ? (lastLine.handoffColor || '#000000')
                : (lastLine.color || '#000000');
            handoffLineColor = lastLine.handoffColor || lastLine.color || '#000000';
        } else if (selectedPlayer.color) {
            // Use the player's color
            lineColor = selectedPlayer.color;
            handoffLineColor = selectedPlayer.color;
        } else {
            // Use default color based on player type
            const defaultColor = type === 'shot' ? '#f57c00' : (selectedPlayer.isDefender ? 'red' : '#000000');
            lineColor = defaultColor;
            handoffLineColor = selectedPlayer.isDefender ? 'red' : '#000000';
        }

        // Create the new line
        const newLine: Line = {
            id: newLineId,
            fromId: playerId,
            toId: undefined,
            type,
            fromX: Math.round(origin.x),
            fromY: Math.round(origin.y),
            toX: Math.round(toX),
            toY: Math.round(toY),
            controlX: Math.round((origin.x + toX) / 2),
            controlY: Math.round((origin.y + toY) / 2),
            order: getNextLineOrder(current.lines),
            controlModified: false,
            color: type === 'handoff' ? 'rgba(255, 255, 255, 0.9)' : lineColor, // White with slight transparency for handoff main line
            handoffColor: type === 'handoff' ? handoffLineColor : undefined, // Use player/last line color for handoff horizontal lines
            uniqueId: `frame${currentFrameIndex}-line${newLineId}`,
        };

        // Update frames with the new line
        setFrames((prev) => {
            const updated = [...prev];
            updated[currentFrameIndex] = {
                ...updated[currentFrameIndex],
                lines: [...updated[currentFrameIndex].lines, newLine],
            };
            return updated;
        });

        // Update state - keep nextLineId as the maximum across all frames
        const maxLineIdAcrossAllFrames = Math.max(...frames.flatMap(f => f.lines.map(l => l.id)), newLineId);
        setNextLineId(maxLineIdAcrossAllFrames + 1);
        setSelectedLineId(newLineId);
        setSelectedPlayerId(null);
        setSelectedConeId(null);
        setSelectedTextBoxId(null);
    };


    const convertToDoubleBezier = () => {
        if (selectedLineId === null) return showSnackbar('PlayBuilder04');
        const selectedLine = currentFrame.lines.find((line) => line.id === selectedLineId);
        if (selectedLine?.type === 'handoff') return showSnackbar('PlayBuilder16'); // Handoff lines cannot be curved
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.lines = current.lines.map((line) => {
                if (line.id === selectedLineId) {
                    const t = 0.5;
                    const midX = bezierPoint(t, line.fromX, line.fromY, line.controlX, line.controlY, line.toX, line.toY).x;
                    const midY = bezierPoint(t, line.fromX, line.fromY, line.controlX, line.controlY, line.toX, line.toY).y;
                    const tFirst = 0.25;
                    const newControlX = bezierPoint(tFirst, line.fromX, line.fromY, line.controlX, line.controlY, line.toX, line.toY).x;
                    const newControlY = bezierPoint(tFirst, line.fromX, line.fromY, line.controlX, line.controlY, line.toX, line.toY).y;
                    const tSecond = 0.75;
                    const controlX2 = bezierPoint(tSecond, line.fromX, line.fromY, line.controlX, line.controlY, line.toX, line.toY).x;
                    const controlY2 = bezierPoint(tSecond, line.fromX, line.fromY, line.controlX, line.controlY, line.toX, line.toY).y;
                    return {
                        ...line,
                        midX,
                        midY,
                        controlX: newControlX,
                        controlY: newControlY,
                        controlX2,
                        controlY2,
                        controlModified: false,
                        control2Modified: false,
                    };
                }
                return line;
            });
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const convertToSingleBezier = () => {
        if (selectedLineId === null) return showSnackbar('PlayBuilder04');
        const selectedLine = currentFrame.lines.find((line) => line.id === selectedLineId);
        if (selectedLine?.type === 'handoff') return showSnackbar('PlayBuilder16'); // Handoff lines cannot be curved
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.lines = current.lines.map((line) => {
                if (line.id === selectedLineId && line.midX !== undefined && line.midY !== undefined && line.controlX2 !== undefined && line.controlY2 !== undefined) {
                    const newControlX = (line.midX - 0.25 * line.fromX - 0.25 * line.toX) / 0.5;
                    const newControlY = (line.midY - 0.25 * line.fromY - 0.25 * line.toY) / 0.5;
                    return {
                        ...line,
                        midX: undefined,
                        midY: undefined,
                        controlX: newControlX,
                        controlY: newControlY,
                        controlX2: undefined,
                        controlY2: undefined,
                        controlModified: true,
                        control2Modified: undefined,
                    };
                }
                return line;
            });
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleDeleteLine = () => {
        if (selectedLineId === null) return showSnackbar('PlayBuilder04'); // "Select a line first"

        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            const selectedLine = current.lines.find((l) => l.id === selectedLineId);
            if (!selectedLine) return prev; // Line not found, no change

            const playerId = selectedLine.fromId; // Get the player from the selected line
            const playerLines = getPlayerLinesInOrder(current.lines, playerId);
            if (playerLines.length === 0) return prev;

            const lineToDelete = playerLines[playerLines.length - 1]; // Always delete the last line
            current.lines = current.lines.filter((l) => l.id !== lineToDelete.id);

            const remainingLines = getPlayerLinesInOrder(current.lines, playerId);
            setSelectedLineId(remainingLines[0]?.id || null); // Select the first remaining line or null

            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleCreateNewFrame = () => {
        const lastFrame = frames[frames.length - 1]; // Use the last frame instead of current frame
        let updatedPlayers = lastFrame.players.map((p) => ({ ...p }));
        const updatedCones = lastFrame.cones.map((c) => ({ ...c })); // Carry over cones
        // NOTE: When creating a new phase/frame we do NOT carry over texts nor shades.
        const updatedTexts: TextBox[] = [];
        const updatedShades: Shade[] = [];

        // Identify all lines sorted by order to process shots and subsequent movements
        const orderedLines = lastFrame.lines.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const shotLines = orderedLines.filter((line) => line.type === 'shot');

        // Update player positions and ball possession
        updatedPlayers = updatedPlayers.map((player) => {
            const playerLines = getPlayerLinesInOrder(lastFrame.lines, player.id); // Already sorted by order
            const lastShotLine = shotLines.find((line) => line.fromId === player.id);

            if (lastShotLine) {
                // Find the last movement line after the shot (if any)
                const shotIndex = playerLines.findIndex((line) => line.id === lastShotLine.id);
                const linesAfterShot = playerLines.slice(shotIndex + 1);
                const lastMovementLineAfterShot = linesAfterShot
                    .filter((line) => ['straight', 'zigzag', 'bar'].includes(line.type)) // Movement lines only
                    .pop();

                if (lastMovementLineAfterShot) {
                    // If there's a movement line after the shot, use its endpoint
                    return {
                        ...player,
                        x: Math.round(lastMovementLineAfterShot.toX),
                        y: Math.round(lastMovementLineAfterShot.toY),
                        hasBall: false, // Player loses the ball after shooting
                    };
                } else {
                    // If no movement after shot, use the shooting position
                    return {
                        ...player,
                        x: Math.round(lastShotLine.fromX),
                        y: Math.round(lastShotLine.fromY),
                        hasBall: false, // Player loses the ball after shooting
                    };
                }
            } else {
                // For non-shooting players, use the last movement line (excluding dashes, shots, and handoffs)
                const lastMovementLine = playerLines
                    .filter((line) => line.type !== 'dashed' && line.type !== 'shot' && line.type !== 'handoff')
                    .pop();
                if (lastMovementLine) {
                    return {
                        ...player,
                        x: Math.round(lastMovementLine.toX),
                        y: Math.round(lastMovementLine.toY),
                        // hasBall remains unchanged unless explicitly modified below
                    };
                }
                return { ...player }; // No change if no relevant lines
            }
        });

        // Handle ball possession after passes and handoffs
        const passAndHandoffLines = orderedLines.filter((line) => line.type === 'dashed' || line.type === 'handoff');

        // Process passes and handoffs to update ball possession
        passAndHandoffLines.forEach((line) => {
            const fromPlayerIndex = updatedPlayers.findIndex((p) => p.id === line.fromId);
            const toPlayerIndex = updatedPlayers.findIndex((p) => p.id === line.toId);
            if (fromPlayerIndex !== -1 && toPlayerIndex !== -1 && updatedPlayers[fromPlayerIndex].hasBall) {
                updatedPlayers[fromPlayerIndex].hasBall = false;
                updatedPlayers[toPlayerIndex].hasBall = true;
            }
        });

        // Create the new frame with sequential ID
        const newFrame: Frame = {
            id: frames.length + 1,
            players: updatedPlayers,
            lines: [],
            cones: updatedCones,
            texts: updatedTexts,
            shades: updatedShades,
        };

        // Add the new frame and update all uniqueIds
        setFrames((prev) => {
            const newFrames = [...prev, newFrame];
            return updateAllUniqueIds(newFrames);
        });

        setCurrentFrameIndex(frames.length);

        // Select only one player with the ball and deselect everything else
        const playerWithBall = updatedPlayers.find((p) => p.hasBall) || updatedPlayers[0]; // Default to first player if no ball
        setSelectedPlayerId(playerWithBall ? playerWithBall.id : null);
        setSelectedLineId(null); // Deselect any selected line
        setSelectedConeId(null); // Deselect any selected cone
        setSelectedTextBoxId(null);
        setSelectedShadeId(null);
        setDragging({ isDragging: false, type: null, id: null }); // Reset dragging state
    };

    const handleDeleteFrame = (index: number) => {
        console.log('handleDeleteFrame called with index:', index);
        console.log('Current frames length:', frames.length);
        console.log('Current frame index:', currentFrameIndex);

        if (frames.length === 1) return showSnackbar('PlayBuilder03');

        const updated = frames.filter((_, i) => i !== index); // Remove the frame at the specified index
        console.log('Frames after filter:', updated.length);

        // Update uniqueIds and ensure sequential frame IDs after deletion
        const updatedFrames = updateAllUniqueIds(updated);
        console.log('Updated frames length:', updatedFrames.length);

        setFrames(updatedFrames);

        // Update current frame index appropriately
        if (currentFrameIndex >= updatedFrames.length) {
            setCurrentFrameIndex(updatedFrames.length - 1);
        } else if (index < currentFrameIndex) {
            setCurrentFrameIndex((prev) => prev - 1);
        }
        // If index === currentFrameIndex, stay on the same position (which is now the next frame)
    };

    const handleDuplicateFrame = (index: number) => {
        const frameToDuplicate = frames[index];

        // Create new unique IDs for all elements - calculate from ALL frames to ensure uniqueness
        // Use frames.length + 1 to ensure unique frame ID based on position
        const newFrameId = frames.length + 1;

        // Calculate max IDs from ALL frames to ensure no conflicts
        const maxPlayerId = Math.max(...frames.flatMap(f => f.players.map(p => p.id)), 0);
        const maxLineId = Math.max(...frames.flatMap(f => f.lines.map(l => l.id)), 0);
        const maxConeId = Math.max(...frames.flatMap(f => f.cones.map(c => c.id)), 0);
        const maxTextBoxId = Math.max(...frames.flatMap(f => (f.texts || []).map(tb => tb.id)), 0);
        const maxShadeId = Math.max(...frames.flatMap(f => (f.shades || []).map(s => s.id)), 0);

        // Use offsets that are guaranteed to be unique
        const newPlayerIdOffset = maxPlayerId + 1000;
        const newLineIdOffset = maxLineId + 1000;
        const newConeIdOffset = maxConeId + 1000;
        const newTextBoxIdOffset = maxTextBoxId + 1000;
        const newShadeIdOffset = maxShadeId + 1000;

        // Duplicate players with new IDs
        const duplicatedPlayers = frameToDuplicate.players.map(player => ({
            ...player,
            id: player.id + newPlayerIdOffset
        }));

        // Duplicate lines with new IDs and updated player references
        const duplicatedLines = frameToDuplicate.lines.map(line => ({
            ...line,
            id: line.id + newLineIdOffset,
            fromId: line.fromId + newPlayerIdOffset,
            toId: line.toId ? line.toId + newPlayerIdOffset : undefined,
            uniqueId: `frame${frames.length}-line${line.id + newLineIdOffset}`
        }));

        // Duplicate cones with new IDs
        const duplicatedCones = frameToDuplicate.cones.map(cone => ({
            ...cone,
            id: cone.id + newConeIdOffset
        }));

        // Duplicate text boxes with new IDs
        const duplicatedTexts = (frameToDuplicate.texts || []).map(tb => ({
            ...tb,
            id: tb.id + newTextBoxIdOffset,
        }));

        const duplicatedShades = (frameToDuplicate.shades || []).map(s => ({
            ...s,
            id: s.id + newShadeIdOffset,
        }));

        // Create the duplicated frame
        const duplicatedFrame: Frame = {
            id: newFrameId,
            players: duplicatedPlayers,
            lines: duplicatedLines,
            cones: duplicatedCones,
            texts: duplicatedTexts,
            shades: duplicatedShades,
            description: frameToDuplicate.description ? `${frameToDuplicate.description}` : ''
        };

        // Add the duplicated frame and update all uniqueIds
        setFrames(prev => {
            const newFrames = [...prev, duplicatedFrame];
            return updateAllUniqueIds(newFrames);
        });

        // Select the new frame
        setCurrentFrameIndex(frames.length);

        // Update next IDs to avoid conflicts
        setNextConeId(Math.max(nextConeId, newConeIdOffset + 1));
        setNextLineId(Math.max(nextLineId, newLineIdOffset + 1));
        setNextTextBoxId(Math.max(nextTextBoxId, newTextBoxIdOffset + 1));
        setNextShadeId(Math.max(nextShadeId, newShadeIdOffset + 1));

        // Clear selections
        setSelectedPlayerId(null);
        setSelectedLineId(null);
        setSelectedConeId(null);
        setSelectedTextBoxId(null);
        setSelectedShadeId(null);
        setDragging({ isDragging: false, type: null, id: null });
    };

    // Helper function to update all uniqueIds based on frame positions
    const updateAllUniqueIds = (framesToUpdate: Frame[]) => {
        return framesToUpdate.map((frame, frameIndex) => ({
            ...frame,
            id: frameIndex + 1, // Ensure sequential frame IDs
            texts: frame.texts || [],
            shades: frame.shades || [],
            lines: frame.lines.map(line => ({
                ...line,
                uniqueId: `frame${frameIndex}-line${line.id}`
            }))
        }));
    };

    // Helper function to clean up duplicate frame IDs and ensure sequential numbering
    const cleanFrameIds = (framesToClean: Frame[]) => {
        return framesToClean.map((frame, index) => ({
            ...frame,
            id: index + 1 // Ensure sequential frame IDs starting from 1
        }));
    };

    const handleReorderFrames = (reorderedFrames: Frame[]) => {
        // Update uniqueIds to match new frame positions
        const updatedFrames = updateAllUniqueIds(reorderedFrames);

        setFrames(updatedFrames);

        // Update current frame index if needed
        if (currentFrameIndex >= updatedFrames.length) {
            setCurrentFrameIndex(updatedFrames.length - 1);
        }
    };

    const handleSavePlay = async () => {
        // const playLocal = {
        //   frames,
        //   currentFrameIndex,
        //   dragging,
        //   selectedPlayerId,
        //   isModalOpen,
        //   nextPlayerId: 1,
        //   nextLineId,
        //   nextConeId, // Include nextConeId
        //   courtType,
        // };
        // const playNode: DrupalEntity = {
        //   type: 'node--play',
        //   id: play.id,
        //   attributes: { field_play_details: JSON.stringify(playLocal) },
        // };
        // const resp = await nodeCRUD(state, dispatch, CRUD.Update, playNode);
        // if (!resp.data) dispatch(getActionSetConfirm(t(resp)));
    };

    const handleToggleBall = () => {
        if (selectedPlayerId === null) return showSnackbar('PlayBuilder05');
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.players = current.players.map((p) =>
                p.id === selectedPlayerId ? { ...p, hasBall: !p.hasBall } : p
            );
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleSelectFrame = (index: number) => {
        setCurrentFrameIndex(index);
        setSelectedPlayerId(null);
        setSelectedLineId(null);
        setSelectedConeId(null);
        setSelectedTextBoxId(null);
        setSelectedShadeId(null);
        setDragging({ isDragging: false, type: null, id: null });
    };

    const handleColorChange = (color: string) => {
        if (selectedLineId === null) return;
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.lines = current.lines.map((line) =>
                line.id === selectedLineId ? { ...line, color } : line
            );
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handleHandoffColorChange = (color: string) => {
        if (selectedLineId === null) return;
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.lines = current.lines.map((line) =>
                line.id === selectedLineId ? { ...line, handoffColor: color } : line
            );
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const handlePlayerColorChange = (color: string) => {
        if (selectedPlayerId === null) return showSnackbar('PlayBuilder05');
        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.players = current.players.map((p) =>
                p.id === selectedPlayerId ? { ...p, color } : p
            );
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    // Handler to update player number (1-3 characters)
    const handlePlayerNumberChange = (newNumber: string) => {
        if (selectedPlayerId === null) return showSnackbar('PlayBuilder05');

        const selectedPlayer = currentFrame.players.find((p) => p.id === selectedPlayerId);
        const isSpecialDefender = selectedPlayer?.isSpecialDefender || false;

        // Check if it's a coach (C or c)
        const isCoachValue = newNumber.toUpperCase() === 'C';

        // For special defenders, save the value as-is (don't process "x" prefix)
        if (isSpecialDefender && !isCoachValue) {
            // Save the number as provided by the user (could be "1", "x1", etc.)
            const numberValue = /^\d+$/.test(newNumber)
                ? parseInt(newNumber, 10)
                : newNumber;

            setFrames((prev) => {
                const updated = [...prev];
                const current = { ...updated[currentFrameIndex] };
                current.players = current.players.map((p) => {
                    if (p.id === selectedPlayerId) {
                        const currentColor = p.color || 'red';
                        return {
                            ...p,
                            number: numberValue as any,
                            isCoach: false,
                            color: currentColor,
                            isSpecialDefender: true,
                            armRotation: p.armRotation,
                        };
                    }
                    return p;
                });
                updated[currentFrameIndex] = current;
                return updated;
            });
            return;
        }

        // For regular players (non-special defenders), use the existing logic
        // Check if the value starts with "x" to determine if it's a defender
        const isDefenderNumber = !isCoachValue && newNumber.toLowerCase().startsWith('x');

        // Extract the number (remove "x" prefix if present)
        const numberOnly = isDefenderNumber ? newNumber.slice(1) : newNumber;

        // For coach, use 0 as the number value, otherwise convert to number if numeric
        const numberValue = isCoachValue
            ? 0
            : /^\d+$/.test(numberOnly)
                ? parseInt(numberOnly, 10)
                : numberOnly;

        setFrames((prev) => {
            const updated = [...prev];
            const current = { ...updated[currentFrameIndex] };
            current.players = current.players.map((p) => {
                if (p.id === selectedPlayerId) {
                    // Preserve the current color (either explicit color or default based on isDefender)
                    const currentColor = p.color || (p.isDefender ? 'red' : 'black');
                    return {
                        ...p,
                        number: numberValue as any,
                        isDefender: isDefenderNumber,
                        isCoach: isCoachValue,
                        color: currentColor,
                    };
                }
                return p;
            });
            updated[currentFrameIndex] = current;
            return updated;
        });
    };

    const currentFrame = frames[currentFrameIndex];
    const selectedLine = currentFrame.lines.find((line) => line.id === selectedLineId);
    const isDoubleBezier = selectedLine?.midX !== undefined;

    const dialogContent = (
        <>
            <Box sx={{
                py: { xs: 1, md: 1.25 },
                px: { xs: 2, md: 4 },
                background: ' #048cec',
                // background: 'linear-gradient(90deg, #0378c4 20%, #f3953d 100%)',
                position: 'relative'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isPortrait ? 'space-between' : 'space-between',
                    width: '100%',
                    gap: isPortrait ? 0.5 : 1,
                    flexWrap: 'nowrap',
                }}>
                    {isPortrait ? (
                        <>
                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setIsModalOpen(true)}
                                    size="small"
                                    sx={{
                                        ...commonStyles.button,
                                        color: "white",
                                        borderColor: 'rgba(255,255,255,0.8)',
                                        borderWidth: 1.5,
                                        px: { xs: 1, sm: 1.5 },
                                        py: 0.5,
                                        minWidth: 'auto',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.15)',
                                            borderColor: 'white',
                                            borderWidth: 1.5,

                                        }
                                    }}
                                >
                                    <DownloadRoundedIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.25 }} />
                                    Video
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleDownloadPdf}
                                    size="small"
                                    sx={{
                                        ...commonStyles.button,
                                        color: "white",
                                        borderColor: 'rgba(255,255,255,0.8)',
                                        borderWidth: 1.5,
                                        px: { xs: 1, sm: 1.5 },
                                        py: 0.5,
                                        minWidth: 'auto',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.15)',
                                            borderColor: 'white',
                                            borderWidth: 1.5,

                                        }
                                    }}
                                >
                                    <DownloadRoundedIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.25 }} />
                                    PDF
                                </Button>
                            </Box>
                            <Typography sx={{
                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                fontWeight: 'bold',
                                color: 'white',
                                textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                flexShrink: 0,
                                textAlign: 'center',
                                display: { xs: 'none', sm: 'block' }
                            }}>
                                {location.pathname === '/createplay' ? t('PlayBuilder06') : t('PlayBuilder15')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        ...commonStyles.button,
                                        color: '#0378c4',
                                        backgroundColor: 'white',
                                        px: { xs: 1, sm: 1.5 },
                                        py: 0.5,
                                        minWidth: 'auto',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa',
                                        }
                                    }}
                                    onClick={() => {
                                        handleSaveClick();
                                    }}
                                >
                                    <SaveRoundedIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.25 }} />
                                    {t('PlayBuilder18')}
                                </Button>
                                <IconButton
                                    onClick={() => setIsConfirmExitDialogOpen(true)}
                                    size="small"
                                    sx={{
                                        color: 'white',
                                        p: 1,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{
                                fontSize: { xs: '16px', md: '20px' },
                                fontWeight: 'bold',
                                color: 'white',
                                textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                textAlign: 'center',
                                flex: 1
                            }}>
                                {location.pathname === '/createplay' ? t('PlayBuilder06') : t('PlayBuilder15')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setIsModalOpen(true)}
                                    size="small"
                                    sx={{
                                        ...commonStyles.button,
                                        color: "white",
                                        borderColor: 'rgba(255,255,255,0.8)',
                                        borderWidth: 1.5,
                                        px: 2,
                                        py: 0.75,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.15)',
                                            borderColor: 'white',
                                            borderWidth: 1.5,
                                        }
                                    }}
                                >
                                    <DownloadRoundedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                    Video
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleDownloadPdf}
                                    size="small"
                                    sx={{
                                        ...commonStyles.button,
                                        color: "white",
                                        borderColor: 'rgba(255,255,255,0.8)',
                                        borderWidth: 1.5,
                                        px: 2,
                                        py: 0.75,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.15)',
                                            borderColor: 'white',
                                            borderWidth: 1.5,
                                        }
                                    }}
                                >
                                    <DownloadRoundedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                    PDF
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        ...commonStyles.button,
                                        color: '#0378c4',
                                        backgroundColor: 'white',
                                        px: 2,
                                        py: 0.75,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa',
                                        }
                                    }}
                                    onClick={() => {
                                        handleSaveClick();
                                    }}
                                >
                                    <SaveRoundedIcon sx={{ fontSize: 18, mr: 0.5 }} /> {t('PlayBuilder18')}
                                </Button>
                                <IconButton
                                    onClick={() => setIsConfirmExitDialogOpen(true)}
                                    size="small"
                                    sx={{
                                        color: 'white',
                                        p: 1,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {/* <Grid container style={{ height: isPortrait ? 'auto' : '100vh', backgroundColor: isPortrait ? 'white' : '#f1f1f1' }}> */}
            < Grid container style={{
                height: 'auto',
                background: isPortrait ? 'white' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                paddingBottom: isPortrait ? '80px' : '0px',
                alignContent: 'flex-start',
                alignItems: 'flex-start',
            }
            }>

                <Grid
                    style={{
                        padding: isPortrait ? '0px' : '10px',
                        borderRight: isPortrait ? 'none' : '1px solid rgba(0,0,0,0.08)',
                        backgroundColor: isPortrait ? 'white' : 'rgba(255,255,255,0.9)',
                        overflowY: 'auto',
                        paddingBottom: isPortrait ? '0px' : '300px',
                        backdropFilter: isPortrait ? 'none' : 'blur(10px)'
                    }}
                    sx={{ order: isPortrait ? 1 : 0 }}
                    xs={isPortrait ? 12 : 3}
                    md={isPortrait ? 12 : 3}>
                    <PhasesSectionPlayBuilder
                        title={props.title || ''}
                        frames={frames}
                        currentFrameIndex={currentFrameIndex}
                        courtType={courtType}
                        handleSelectFrame={handleSelectFrame}
                        handleDeleteFrame={handleDeleteFrame}
                        handleCreateNewFrame={handleCreateNewFrame}
                        handleDuplicateFrame={handleDuplicateFrame}
                        handleReorderFrames={handleReorderFrames}
                        portrait={isPortrait}
                        use3DPlayers={use3DPlayers}
                    />
                </Grid>

                <Grid
                    style={{ position: 'relative', maxWidth: `${MAX_SVG_WIDTH}px`, margin: '0 auto', paddingBottom: isPortrait ? '0px' : '300px' }}
                    sx={{ order: isPortrait ? 2 : 0 }}
                    xs={12}
                    md={6}>
                    {!isPortrait && (
                        <Typography sx={{
                            fontWeight: 'bold',
                            textAlign: 'center',
                            pt: 1,
                            pb: 1,
                            fontSize: '18px',
                            color: '#2c3e50',
                            textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}>
                            {props.title || ''}
                        </Typography>
                    )}

                    <Box sx={{
                        backgroundColor: 'white',
                        borderRadius: 3,
                        // padding: 0,
                        // margin: 1,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        position: 'relative'
                    }}>
                        <svg
                            key={`svg-frame-${currentFrameIndex}-${currentFrame.id}-${currentFrame.lines.map(l => `${l.id}-${l.color || 'default'}`).join('-')}`}
                            ref={svgRef}
                            onMouseMove={handleMove}
                            onTouchMove={handleMove}
                            onMouseDown={() => {
                                // Click-away: deselect everything. If selected textbox is empty, delete it.
                                if (selectedTextBoxId !== null) {
                                    const tb = (currentFrame.texts || []).find((t) => t.id === selectedTextBoxId);
                                    if (tb && (tb.text || '').trim().length === 0) {
                                        setFrames((prev) => {
                                            const updated = [...prev];
                                            const current = { ...updated[currentFrameIndex] };
                                            current.texts = (current.texts || []).filter((t) => t.id !== selectedTextBoxId);
                                            updated[currentFrameIndex] = current;
                                            return updated;
                                        });
                                    }
                                }
                                setSelectedPlayerId(null);
                                setSelectedLineId(null);
                                setSelectedConeId(null);
                                setSelectedTextBoxId(null);
                                setSelectedShadeId(null);
                            }}
                            onTouchStart={() => {
                                if (selectedTextBoxId !== null) {
                                    const tb = (currentFrame.texts || []).find((t) => t.id === selectedTextBoxId);
                                    if (tb && (tb.text || '').trim().length === 0) {
                                        setFrames((prev) => {
                                            const updated = [...prev];
                                            const current = { ...updated[currentFrameIndex] };
                                            current.texts = (current.texts || []).filter((t) => t.id !== selectedTextBoxId);
                                            updated[currentFrameIndex] = current;
                                            return updated;
                                        });
                                    }
                                }
                                setSelectedPlayerId(null);
                                setSelectedLineId(null);
                                setSelectedConeId(null);
                                setSelectedTextBoxId(null);
                                setSelectedShadeId(null);
                            }}
                            onMouseUp={handleEnd}
                            onTouchEnd={handleEnd}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            viewBox={`0 0 ${courtWidth} ${courtHeight}`}
                            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: isPortrait ? 0 : 8, touchAction: 'none' }}
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <Court courtType={courtType} />

                            <defs>
                                {currentFrame.lines.map((line) => {
                                    const lineColor = line.color || '#000000';
                                    const arrowColor = line.type === 'handoff' ? 'rgba(255, 255, 255, 0.9)' : lineColor;
                                    const cleanedArrowColor = arrowColor.replace(/[^a-z0-9]/gi, '_');
                                    const cleanedColor = lineColor.replace(/[^a-z0-9]/gi, '_');

                                    const arrowheadId = `arrowhead-${currentFrame.id}-${line.id}-${cleanedArrowColor}`;
                                    const barEndId = `barEnd-${currentFrame.id}-${line.id}-${cleanedColor}`;

                                    // Hovered color markers
                                    const hoveredArrowColor = line.type === 'handoff' ? 'rgba(255, 255, 255, 0.9)' : COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER;
                                    const cleanedHoveredArrowColor = line.type === 'handoff' ? '25525525509' : COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER.replace('#', '');
                                    const hoveredArrowheadId = `arrowhead-${currentFrame.id}-${line.id}-${cleanedHoveredArrowColor}`;
                                    const hoveredBarEndId = `barEnd-${currentFrame.id}-${line.id}-${COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER.replace('#', '')}`;

                                    const markerSize = FRAME_RENDER_CONFIG.line.markerSize;

                                    return (
                                        <React.Fragment key={`defs-${line.id}`}>
                                            {/* Normal color markers */}
                                            <marker
                                                id={arrowheadId}
                                                markerWidth={markerSize}
                                                markerHeight={markerSize * 0.7}
                                                refX={markerSize * 0.75}
                                                refY={markerSize * 0.35}
                                                orient="auto"
                                                markerUnits="strokeWidth"
                                            >
                                                <polygon
                                                    points={`${markerSize * 0.9} ${markerSize * 0.35}, ${markerSize * 0.2} ${markerSize * 0.1}, ${markerSize * 0.4} ${markerSize * 0.35}, ${markerSize * 0.2} ${markerSize * 0.6}`}
                                                    fill={arrowColor}
                                                />
                                            </marker>
                                            <marker
                                                id={barEndId}
                                                markerWidth="1"
                                                markerHeight={markerSize * 1.0}
                                                refX="0.25"
                                                refY={(markerSize * 1.0) / 2}
                                                orient="auto"
                                                markerUnits="strokeWidth"
                                            >
                                                <rect x="0" y="0" width="1" height={markerSize * 1.0} fill={lineColor} />
                                            </marker>
                                            {/* Hovered color markers */}
                                            <marker
                                                id={hoveredArrowheadId}
                                                markerWidth={markerSize}
                                                markerHeight={markerSize * 0.7}
                                                refX={markerSize * 0.75}
                                                refY={markerSize * 0.35}
                                                orient="auto"
                                                markerUnits="strokeWidth"
                                            >
                                                <polygon
                                                    points={`${markerSize * 0.9} ${markerSize * 0.35}, ${markerSize * 0.2} ${markerSize * 0.1}, ${markerSize * 0.4} ${markerSize * 0.35}, ${markerSize * 0.2} ${markerSize * 0.6}`}
                                                    fill={hoveredArrowColor}
                                                />
                                            </marker>
                                            <marker
                                                id={hoveredBarEndId}
                                                markerWidth="1"
                                                markerHeight={markerSize * 1.0}
                                                refX="0.25"
                                                refY={(markerSize * 1.0) / 2}
                                                orient="auto"
                                                markerUnits="strokeWidth"
                                            >
                                                <rect x="0" y="0" width="1" height={markerSize * 1.0} fill={COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER} />
                                            </marker>
                                        </React.Fragment>
                                    );
                                })}
                            </defs>

                            {/* Render Shades (always behind everything) */}
                            {(currentFrame.shades || []).map((sh) => {
                                const isSelected = selectedShadeId === sh.id;
                                const base = normalizeShadeColor(sh.color);
                                const FILL = hexToRgba(base, 0.24); // slightly darker than before
                                const BORDER = darkenHex(base, 0.35); // same hue, darker

                                const startShadeMove = (e: any) => {
                                    e.stopPropagation();
                                    setShadeResizeCorner(null);
                                    handleStart(e, 'shade', sh.id);
                                };
                                const startShadeResize = (corner: 'tl' | 'tr' | 'bl' | 'br') => (e: any) => {
                                    e.stopPropagation();
                                    setShadeResizeCorner(corner);
                                    handleStart(e, 'shadeResize', sh.id);
                                };

                                const cornerPoints = [
                                    { corner: 'tl' as const, x: sh.x, y: sh.y, cursor: 'nwse-resize' },
                                    { corner: 'tr' as const, x: sh.x + sh.width, y: sh.y, cursor: 'nesw-resize' },
                                    { corner: 'bl' as const, x: sh.x, y: sh.y + sh.height, cursor: 'nesw-resize' },
                                    { corner: 'br' as const, x: sh.x + sh.width, y: sh.y + sh.height, cursor: 'nwse-resize' },
                                ];

                                return (
                                    <g key={`shade-${sh.id}`}>
                                        {sh.shape === 'rect' ? (
                                            <rect
                                                x={sh.x}
                                                y={sh.y}
                                                width={sh.width}
                                                height={sh.height}
                                                rx={10}
                                                ry={10}
                                                fill={FILL}
                                                stroke={isSelected ? BORDER : 'none'}
                                                strokeWidth={isSelected ? 1.2 : 0}
                                                onMouseDown={startShadeMove}
                                                onTouchStart={startShadeMove}
                                                style={{ cursor: 'move' }}
                                            />
                                        ) : (
                                            <ellipse
                                                cx={sh.x + sh.width / 2}
                                                cy={sh.y + sh.height / 2}
                                                rx={Math.max(1, sh.width / 2)}
                                                ry={Math.max(1, sh.height / 2)}
                                                fill={FILL}
                                                stroke={isSelected ? BORDER : 'none'}
                                                strokeWidth={isSelected ? 1.2 : 0}
                                                onMouseDown={startShadeMove}
                                                onTouchStart={startShadeMove}
                                                style={{ cursor: 'move' }}
                                            />
                                        )}

                                        {/* Selection handles */}
                                        {isSelected && cornerPoints.map((h) => (
                                            <g key={`shade-handle-${sh.id}-${h.corner}`}>
                                                <circle
                                                    cx={h.x}
                                                    cy={h.y}
                                                    r={6 * scale}
                                                    fill="#007BFF"
                                                    stroke="white"
                                                    strokeWidth={2}
                                                    style={{ cursor: h.cursor }}
                                                    onMouseDown={startShadeResize(h.corner)}
                                                    onTouchStart={startShadeResize(h.corner)}
                                                />
                                                <circle
                                                    cx={h.x}
                                                    cy={h.y}
                                                    r={15 * scale}
                                                    fill="transparent"
                                                    style={{ cursor: h.cursor }}
                                                    onMouseDown={startShadeResize(h.corner)}
                                                    onTouchStart={startShadeResize(h.corner)}
                                                />
                                            </g>
                                        ))}
                                    </g>
                                );
                            })}

                            {/* Render Text Boxes */}
                            {(currentFrame.texts || []).map((tb) => {
                                const isSelected = selectedTextBoxId === tb.id;
                                const padding = 6;
                                const innerW = Math.max(1, tb.width - padding * 2);
                                const innerH = Math.max(1, tb.height - padding * 2);
                                const HANDLE_FILL = '#007BFF';
                                const HANDLE_STROKE = 'white';

                                const startResize = (corner: 'tl' | 'tr' | 'bl' | 'br') => (e: any) => {
                                    e.stopPropagation();
                                    setTextResizeCorner(corner);
                                    handleStart(e, 'textResize', tb.id);
                                };

                                const startMove = (e: any) => {
                                    // Start move from anywhere (including inside) — also works when not selected
                                    e.stopPropagation();
                                    setTextResizeCorner(null);
                                    handleStart(e, 'text', tb.id);
                                };

                                const fontSize = tb.fontSize || 14;

                                if (!isSelected) {
                                    // Not selected: render ONLY text (no background/border).
                                    // Use foreignObject+div so wrapping/margins match the textarea when selected (avoid line jump).
                                    return (
                                        <g key={`textbox-${tb.id}`}>
                                            <foreignObject
                                                x={tb.x + padding}
                                                y={tb.y + padding}
                                                width={innerW}
                                                height={innerH}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        color: '#111111',
                                                        fontSize: `${fontSize}px`,
                                                        lineHeight: '1.4',
                                                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                        fontWeight: 'normal',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        overflow: 'hidden',
                                                        background: 'transparent',
                                                        textAlign: 'left',
                                                        cursor: 'pointer',
                                                        userSelect: 'none',
                                                        display: 'inline-block',
                                                        maxWidth: '100%',
                                                    }}
                                                    onMouseDown={(e) => startMove(e)}
                                                    onTouchStart={(e) => startMove(e)}
                                                >
                                                    {tb.text}
                                                </div>
                                            </foreignObject>
                                        </g>
                                    );
                                }

                                // Selected: show background + border + textarea + corner handles
                                return (
                                    <g
                                        key={`textbox-${tb.id}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleTextBoxClick(tb.id);
                                        }}
                                    >
                                        <rect
                                            x={tb.x}
                                            y={tb.y}
                                            width={tb.width}
                                            height={tb.height}
                                            rx={4}
                                            ry={4}
                                            fill={'rgba(255,255,255,0.85)'}
                                            stroke="#00398f"
                                            strokeWidth={1.2}
                                        />

                                        <foreignObject
                                            x={tb.x + padding}
                                            y={tb.y + padding}
                                            width={innerW}
                                            height={innerH}
                                        >
                                            <textarea
                                                ref={(el) => { textAreaRefs.current[tb.id] = el; }}
                                                value={tb.text}
                                                onChange={(e) => {
                                                    setFrames((prev) => {
                                                        const updated = [...prev];
                                                        const current = { ...updated[currentFrameIndex] };
                                                        current.texts = (current.texts || []).map((tbox) =>
                                                            tbox.id === tb.id ? { ...tbox, text: e.target.value } : tbox
                                                        );
                                                        updated[currentFrameIndex] = current;
                                                        return updated;
                                                    });
                                                }}
                                                onMouseDown={(e) => {
                                                    // Click = focus (handled on mouseup), drag = move (threshold in handleMove)
                                                    startMove(e);
                                                }}
                                                onTouchStart={(e) => {
                                                    startMove(e);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    border: 'none',
                                                    outline: 'none',
                                                    resize: 'none',
                                                    background: 'transparent',
                                                    color: '#111111',
                                                    fontSize: `${fontSize}px`,
                                                    lineHeight: '1.4',
                                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                    fontWeight: 'normal',
                                                    padding: 0,
                                                    margin: 0,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    textAlign: 'left',
                                                }}
                                            />
                                        </foreignObject>

                                        {/* Corner handles (style like line points) */}
                                        {([
                                            { corner: 'tl', x: tb.x, y: tb.y, cursor: 'nwse-resize' },
                                            { corner: 'tr', x: tb.x + tb.width, y: tb.y, cursor: 'nesw-resize' },
                                            { corner: 'bl', x: tb.x, y: tb.y + tb.height, cursor: 'nesw-resize' },
                                            { corner: 'br', x: tb.x + tb.width, y: tb.y + tb.height, cursor: 'nwse-resize' },
                                        ] as const).map((h) => (
                                            <g key={`tb-handle-${tb.id}-${h.corner}`}>
                                                <circle
                                                    cx={h.x}
                                                    cy={h.y}
                                                    r={6 * scale}
                                                    fill={HANDLE_FILL}
                                                    stroke={HANDLE_STROKE}
                                                    strokeWidth={2}
                                                    style={{ cursor: h.cursor }}
                                                    onMouseDown={startResize(h.corner)}
                                                    onTouchStart={startResize(h.corner)}
                                                />
                                                <circle
                                                    cx={h.x}
                                                    cy={h.y}
                                                    r={15 * scale}
                                                    fill="transparent"
                                                    style={{ cursor: h.cursor }}
                                                    onMouseDown={startResize(h.corner)}
                                                    onTouchStart={startResize(h.corner)}
                                                />
                                            </g>
                                        ))}
                                    </g>
                                );
                            })}

                            {/* Render Cones */}
                            {currentFrame.cones.map((cone) => (
                                <g
                                    key={cone.id}
                                    onMouseDown={(e) => handleStart(e, 'cone', cone.id)}
                                    onTouchStart={(e) => handleStart(e, 'cone', cone.id)}
                                    onClick={() => handleConeClick(cone.id)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* Transparent clickable area */}
                                    <rect
                                        x={cone.x - 16 * scale}
                                        y={cone.y - 16 * scale}
                                        width={32 * scale}
                                        height={32 * scale}
                                        fill="transparent"
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <foreignObject
                                        x={cone.x - 12 * scale}
                                        y={cone.y - 12 * scale}
                                        width={24 * scale}
                                        height={24 * scale}
                                    >
                                        <ConeIcon size={24 * scale} color={cone.color} />
                                    </foreignObject>
                                    {(dragging.type === 'cone' && dragging.id === cone.id) || selectedConeId === cone.id ? (
                                        <rect
                                            x={cone.x - 16 * scale}
                                            y={cone.y - 16 * scale}
                                            width={32 * scale}
                                            height={32 * scale}
                                            rx={2}
                                            ry={2}
                                            fill="none"
                                            stroke="black"
                                            strokeWidth={1}
                                            strokeDasharray="4,2"
                                        >
                                            <animate attributeName="stroke-dashoffset" from="0" to="6" dur="2s" repeatCount="indefinite" />
                                        </rect>
                                    ) : null}
                                </g>
                            ))}

                            {currentFrame.players.map((player) => (
                                <g
                                    key={player.id}
                                    onMouseDown={(e) => handleStart(e, 'player', player.id)}
                                    onTouchStart={(e) => handleStart(e, 'player', player.id)}
                                    onClick={() => handlePlayerClick(player.id)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <circle
                                        cx={player.x}
                                        cy={player.y}
                                        r={12 * scale}
                                        fill={isDraggingLineEnd ? '#F0A85F' : 'transparent'}
                                        stroke={isDraggingLineEnd ? '#DD7720' : 'transparent'}
                                        strokeWidth={1}
                                        strokeDasharray="4,2"
                                    >
                                        {isDraggingLineEnd && (
                                            <>
                                                <animate attributeName="fill" values="#F0A85F;#F6C28C;#F0A85F" dur="2s" repeatCount="indefinite" />
                                                <animate attributeName="stroke-dashoffset" from="0" to="6" dur="2s" repeatCount="indefinite" />
                                            </>
                                        )}
                                    </circle>

                                    {selectedPlayerId === player.id && (
                                        <rect
                                            x={player.x - 20 * scale}
                                            y={player.y - 20 * scale}
                                            width={40 * scale}
                                            height={40 * scale}
                                            rx={2}
                                            ry={2}
                                            fill="#f1f1f1"
                                            stroke="black"
                                            strokeWidth={1}
                                            strokeDasharray="4,2"
                                        >
                                            <animate attributeName="stroke-dashoffset" from="0" to="6" dur="2s" repeatCount="indefinite" />
                                        </rect>
                                    )}
                                    {player.hasBall && (
                                        <circle
                                            cx={player.x}
                                            cy={player.y}
                                            r={FRAME_RENDER_CONFIG.player.ballRadius * scale}
                                            stroke={player.color || (player.isDefender ? 'red' : 'black')}
                                            strokeWidth={FRAME_RENDER_CONFIG.player.ballStrokeWidth * scale}
                                            fill="none"
                                        />
                                    )}
                                    {/* Special Defender rendering: circle around number and two small semicircles for arms */}
                                    {player.isSpecialDefender && (
                                        <>
                                            {/* Circle around the number with red background */}
                                            <circle
                                                cx={player.x}
                                                cy={player.y}
                                                r={12 * scale}
                                                fill={player.color || 'red'}
                                                stroke={player.color || 'red'}
                                                strokeWidth={2 * scale}
                                            />
                                            {/* Arms group with rotation */}
                                            <g transform={`translate(${player.x}, ${player.y}) rotate(${player.armRotation || 0})`}>
                                                {/* Left arm - small semicircle positioned outside and angled */}
                                                <g transform={`translate(${-18 * scale}, ${2 * scale}) rotate(-30)`}>
                                                    <path
                                                        d={`M -${13 * scale} ${2 * scale} A ${13 * scale} ${4 * scale} 0 0 1 ${13 * scale} ${2 * scale}`}
                                                        fill="none"
                                                        stroke={player.color || 'red'}
                                                        strokeWidth={3.5 * scale}
                                                        strokeLinecap="round"
                                                    />
                                                </g>
                                                {/* Right arm - small semicircle positioned outside and angled */}
                                                <g transform={`translate(${18 * scale}, ${2 * scale}) rotate(30)`}>
                                                    <path
                                                        d={`M -${13 * scale} ${2 * scale} A ${13 * scale} ${4 * scale} 0 0 1 ${13 * scale} ${2 * scale}`}
                                                        fill="none"
                                                        stroke={player.color || 'red'}
                                                        strokeWidth={3.5 * scale}
                                                        strokeLinecap="round"
                                                    />
                                                </g>
                                            </g>
                                            {/* Rotation control button - visible when selected */}
                                            {(selectedPlayerId === player.id || (dragging.type === 'armRotation' && dragging.id === player.id)) && (
                                                <g>
                                                    {/* Calculate button position (above the circle) */}
                                                    {(() => {
                                                        const rotation = (player.armRotation || 0) * (Math.PI / 180);
                                                        const buttonDistance = 40 * scale; // Distance from center (increased further)
                                                        const buttonX = player.x + Math.sin(rotation) * buttonDistance;
                                                        const buttonY = player.y - Math.cos(rotation) * buttonDistance;
                                                        // Player circle radius
                                                        const playerCircleRadius = 12 * scale;
                                                        return (
                                                            <>
                                                                {/* Calculate line start point at bottom of player circle and end point at bottom of dashed circle */}
                                                                {(() => {
                                                                    // Vector from player to button center
                                                                    const dx = buttonX - player.x;
                                                                    const dy = buttonY - player.y;
                                                                    const distance = Math.hypot(dx, dy);
                                                                    // Normalize direction
                                                                    const dirX = dx / distance;
                                                                    const dirY = dy / distance;
                                                                    // Circle radius (visible circle is smaller, but hit area remains larger)
                                                                    const circleRadius = 8 * scale; // Smaller visible circle
                                                                    // Point on player circle edge (bottom, in direction to button)
                                                                    const lineStartX = player.x + dirX * playerCircleRadius;
                                                                    const lineStartY = player.y + dirY * playerCircleRadius;
                                                                    // Point on circle edge in direction from player (bottom of circle)
                                                                    const lineEndX = buttonX - dirX * circleRadius;
                                                                    const lineEndY = buttonY - dirY * circleRadius;

                                                                    return (
                                                                        <>
                                                                            {/* Solid line from bottom of player circle to bottom of circle */}
                                                                            <line
                                                                                x1={lineStartX}
                                                                                y1={lineStartY}
                                                                                x2={lineEndX}
                                                                                y2={lineEndY}
                                                                                stroke={player.color || 'red'}
                                                                                strokeWidth={1.5 * scale}
                                                                                opacity={0.6}
                                                                                style={{ pointerEvents: 'none' }}
                                                                            />
                                                                        </>
                                                                    );
                                                                })()}
                                                                {/* Solid circle around button to indicate rotation - smaller but same color as defender */}
                                                                <circle
                                                                    cx={buttonX}
                                                                    cy={buttonY}
                                                                    r={8 * scale}
                                                                    fill="none"
                                                                    stroke={player.color || 'red'}
                                                                    strokeWidth={2 * scale}
                                                                    opacity={0.6}
                                                                    style={{ pointerEvents: 'none' }}
                                                                />
                                                                {/* Clickable button circle - transparent with larger hit area */}
                                                                <circle
                                                                    cx={buttonX}
                                                                    cy={buttonY}
                                                                    r={10 * scale}
                                                                    fill="transparent"
                                                                    stroke="none"
                                                                    style={{ cursor: dragging.type === 'armRotation' && dragging.id === player.id ? 'grabbing' : 'grab' }}
                                                                    onMouseDown={(e) => {
                                                                        e.stopPropagation();
                                                                        handleStart(e, 'armRotation', player.id);
                                                                    }}
                                                                    onTouchStart={(e) => {
                                                                        e.stopPropagation();
                                                                        handleStart(e, 'armRotation', player.id);
                                                                    }}
                                                                />
                                                                {/* Larger invisible hit area for easier clicking */}
                                                                <circle
                                                                    cx={buttonX}
                                                                    cy={buttonY}
                                                                    r={12 * scale}
                                                                    fill="transparent"
                                                                    stroke="none"
                                                                    style={{ cursor: dragging.type === 'armRotation' && dragging.id === player.id ? 'grabbing' : 'grab' }}
                                                                    onMouseDown={(e) => {
                                                                        e.stopPropagation();
                                                                        handleStart(e, 'armRotation', player.id);
                                                                    }}
                                                                    onTouchStart={(e) => {
                                                                        e.stopPropagation();
                                                                        handleStart(e, 'armRotation', player.id);
                                                                    }}
                                                                />
                                                            </>
                                                        );
                                                    })()}
                                                </g>
                                            )}
                                        </>
                                    )}
                                    <text
                                        x={player.x}
                                        y={player.y}
                                        textAnchor="middle"
                                        alignmentBaseline="central"
                                        fontSize={(() => {
                                            const displayText = player.isCoach ? 'C' : player.isSpecialDefender ? String(player.number) : player.isDefender ? `x${player.number}` : String(player.number);
                                            const charCount = displayText.length;
                                            const baseSize = FRAME_RENDER_CONFIG.player.getFontSize(charCount) * scale;
                                            return player.isSpecialDefender ? baseSize * 0.75 : baseSize;
                                        })()}
                                        fontWeight="bold"
                                        fill={player.isSpecialDefender ? 'white' : (player.color || (player.isDefender ? 'red' : 'black'))}
                                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                                    >
                                        {player.isCoach ? 'C' : player.isSpecialDefender ? player.number : player.isDefender ? `x${player.number}` : player.number}
                                    </text>
                                </g>
                            ))}

                            {currentFrame.lines.map((line) => {
                                const adjustedFrom = { x: line.fromX, y: line.fromY };
                                const adjustedTo = { x: line.toX, y: line.toY };
                                const pathD = generatePathD(line, adjustedFrom, adjustedTo, currentFrame.lines, false, scale);
                                const showPoints = selectedLineId === line.id;
                                const isHovered = hoveredLineId === line.id;

                                return (
                                    <g key={line.id}>
                                        <path
                                            d={pathD}
                                            stroke="transparent"
                                            strokeWidth={10}
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            pointerEvents="stroke"
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLineClick(line.id);
                                            }}
                                        />
                                        <path
                                            d={pathD}
                                            stroke={isHovered ? COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER : (line.color || '#000000')}
                                            strokeWidth={FRAME_RENDER_CONFIG.line.strokeWidth}
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeDasharray={line.type === 'dashed' || line.type === 'shot' || line.type === 'handoff' ? '5,5' : 'none'}
                                            markerEnd={
                                                line.type === 'bar'
                                                    ? `url(#barEnd-${currentFrame.id}-${line.id}-${(isHovered ? COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER.replace('#', '') : (line.color || '#000000').replace(/[^a-z0-9]/gi, '_'))})`
                                                    : (line.type === 'straight' || line.type === 'zigzag' || line.type === 'dashed')
                                                        ? `url(#arrowhead-${currentFrame.id}-${line.id}-${(isHovered ? COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER.replace('#', '') : (line.color || '#000000').replace(/[^a-z0-9]/gi, '_'))})`
                                                        : undefined
                                            }
                                            pointerEvents="none"
                                        />
                                        {line.type === 'handoff' && (
                                            <>
                                                {/* Calculate the midpoint of the handoff line */}
                                                {(() => {
                                                    const midX = (adjustedFrom.x + adjustedTo.x) / 2;
                                                    const midY = (adjustedFrom.y + adjustedTo.y) / 2;
                                                    // Fixed orientation for handoff lines (1 horizontal line with 2 vertical lines at 20% and 80%)
                                                    const horizontalLineLength = 20; // Length of horizontal line
                                                    const verticalLineLength = 20; // Length of vertical lines

                                                    // Horizontal line (always horizontal)
                                                    const horizontalStartX = midX - horizontalLineLength / 2;
                                                    const horizontalStartY = midY;
                                                    const horizontalEndX = midX + horizontalLineLength / 2;
                                                    const horizontalEndY = midY;

                                                    // First vertical line at 30% of horizontal line
                                                    const firstVerticalX = midX - horizontalLineLength / 2 + (horizontalLineLength * 0.3);
                                                    const line1StartX = firstVerticalX;
                                                    const line1StartY = midY - verticalLineLength / 2;
                                                    const line1EndX = firstVerticalX;
                                                    const line1EndY = midY + verticalLineLength / 2;

                                                    // Second vertical line at 70% of horizontal line
                                                    const secondVerticalX = midX - horizontalLineLength / 2 + (horizontalLineLength * 0.7);
                                                    const line2StartX = secondVerticalX;
                                                    const line2StartY = midY - verticalLineLength / 2;
                                                    const line2EndX = secondVerticalX;
                                                    const line2EndY = midY + verticalLineLength / 2;

                                                    return (
                                                        <>
                                                            {/* Invisible clickable area for handoff symbol */}
                                                            <rect
                                                                x={horizontalStartX - 5}
                                                                y={midY - verticalLineLength / 2 - 5}
                                                                width={horizontalLineLength + 10}
                                                                height={verticalLineLength + 10}
                                                                fill="transparent"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleLineClick(line.id);
                                                                }}
                                                            />
                                                            <line
                                                                x1={horizontalStartX}
                                                                y1={horizontalStartY}
                                                                x2={horizontalEndX}
                                                                y2={horizontalEndY}
                                                                stroke={isHovered ? COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER : (line.handoffColor || "#000000")}
                                                                strokeWidth={FRAME_RENDER_CONFIG.line.strokeWidth}
                                                                strokeLinecap="round"
                                                            />
                                                            <line
                                                                x1={line1StartX}
                                                                y1={line1StartY}
                                                                x2={line1EndX}
                                                                y2={line1EndY}
                                                                stroke={isHovered ? COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER : (line.handoffColor || "#000000")}
                                                                strokeWidth={FRAME_RENDER_CONFIG.line.strokeWidth}
                                                                strokeLinecap="round"
                                                            />
                                                            <line
                                                                x1={line2StartX}
                                                                y1={line2StartY}
                                                                x2={line2EndX}
                                                                y2={line2EndY}
                                                                stroke={isHovered ? COLOR_WHEN_HOVERING_LINES_FROM_LINES_ORDER : (line.handoffColor || "#000000")}
                                                                strokeWidth={FRAME_RENDER_CONFIG.line.strokeWidth}
                                                                strokeLinecap="round"
                                                            />
                                                        </>
                                                    );
                                                })()}
                                            </>
                                        )}
                                        {line.type === 'shot' && (
                                            <>
                                                {/* Visible Shot Circle */}
                                                <circle
                                                    cx={line.toX}
                                                    cy={line.toY}
                                                    r={8 * scale}
                                                    fill="none"
                                                    stroke={line.color || '#000000'}
                                                    strokeWidth={FRAME_RENDER_CONFIG.line.strokeWidth}
                                                />
                                                {/* Transparent Selection Circle */}
                                                <circle
                                                    cx={line.toX}
                                                    cy={line.toY}
                                                    r={15 * scale}
                                                    fill="transparent"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={(e) => handleStart(e, 'end', line.id)}
                                                    onTouchStart={(e) => handleStart(e, 'end', line.id)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleLineClick(line.id);
                                                    }}
                                                />
                                            </>
                                        )}
                                        {showPoints && line.type !== 'handoff' && (
                                            <>
                                                <circle
                                                    cx={bezierPoint(0.5, line.fromX, line.fromY, line.controlX, line.controlY, line.midX ?? line.toX, line.midY ?? line.toY).x}
                                                    cy={bezierPoint(0.5, line.fromX, line.fromY, line.controlX, line.controlY, line.midX ?? line.toX, line.midY ?? line.toY).y}
                                                    r={6 * scale}
                                                    fill="#007BFF"
                                                    stroke="white"
                                                    strokeWidth={2}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={(e) => handleStart(e, 'control', line.id)}
                                                    onTouchStart={(e) => handleStart(e, 'control', line.id)}
                                                />
                                                <circle
                                                    cx={bezierPoint(0.5, line.fromX, line.fromY, line.controlX, line.controlY, line.midX ?? line.toX, line.midY ?? line.toY).x}
                                                    cy={bezierPoint(0.5, line.fromX, line.fromY, line.controlX, line.controlY, line.midX ?? line.toX, line.midY ?? line.toY).y}
                                                    r={15 * scale}
                                                    fill="transparent"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={(e) => handleStart(e, 'control', line.id)}
                                                    onTouchStart={(e) => handleStart(e, 'control', line.id)}
                                                />
                                                {line.midX && line.midY && line.controlX2 && line.controlY2 && (
                                                    <>
                                                        <circle
                                                            cx={line.midX}
                                                            cy={line.midY}
                                                            r={4 * scale}
                                                            fill="#FF7720"
                                                            stroke="white"
                                                            strokeWidth={2}
                                                            style={{ cursor: 'pointer' }}
                                                            onMouseDown={(e) => handleStart(e, 'mid', line.id)}
                                                            onTouchStart={(e) => handleStart(e, 'mid', line.id)}
                                                        />
                                                        <circle
                                                            cx={line.midX}
                                                            cy={line.midY}
                                                            r={15 * scale}
                                                            fill="transparent"
                                                            style={{ cursor: 'pointer' }}
                                                            onMouseDown={(e) => handleStart(e, 'mid', line.id)}
                                                            onTouchStart={(e) => handleStart(e, 'mid', line.id)}
                                                        />
                                                        <circle
                                                            cx={bezierPoint(0.5, line.midX, line.midY, line.controlX2, line.controlY2, line.toX, line.toY).x}
                                                            cy={bezierPoint(0.5, line.midX, line.midY, line.controlX2, line.controlY2, line.toX, line.toY).y}
                                                            r={6 * scale}
                                                            fill="#007BFF"
                                                            stroke="white"
                                                            strokeWidth={2}
                                                            style={{ cursor: 'pointer' }}
                                                            onMouseDown={(e) => handleStart(e, 'control2', line.id)}
                                                            onTouchStart={(e) => handleStart(e, 'control2', line.id)}
                                                        />
                                                        <circle
                                                            cx={bezierPoint(0.5, line.midX, line.midY, line.controlX2, line.controlY2, line.toX, line.toY).x}
                                                            cy={bezierPoint(0.5, line.midX, line.midY, line.controlX2, line.controlY2, line.toX, line.toY).y}
                                                            r={15 * scale}
                                                            fill="transparent"
                                                            style={{ cursor: 'pointer' }}
                                                            onMouseDown={(e) => handleStart(e, 'control2', line.id)}
                                                            onTouchStart={(e) => handleStart(e, 'control2', line.id)}
                                                        />
                                                    </>
                                                )}
                                                <circle
                                                    cx={line.toX}
                                                    cy={line.toY}
                                                    r={line.type === 'dashed' ? 12 * scale : 6 * scale}
                                                    fill={line.type === 'dashed' ? 'rgba(229, 143, 71, 0.5)' : '#007BFF'}
                                                    stroke="white"
                                                    strokeWidth={2}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={(e) => handleStart(e, 'end', line.id)}
                                                    onTouchStart={(e) => handleStart(e, 'end', line.id)}
                                                />
                                                <circle
                                                    cx={line.toX}
                                                    cy={line.toY}
                                                    r={15 * scale}
                                                    fill="transparent"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={(e) => handleStart(e, 'end', line.id)}
                                                    onTouchStart={(e) => handleStart(e, 'end', line.id)}
                                                />
                                            </>
                                        )}
                                        {showPoints && line.type === 'handoff' && (
                                            <>
                                                <circle
                                                    cx={line.toX}
                                                    cy={line.toY}
                                                    r={12 * scale}
                                                    fill='rgba(229, 143, 71, 0.5)'
                                                    stroke="white"
                                                    strokeWidth={2}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={(e) => handleStart(e, 'end', line.id)}
                                                    onTouchStart={(e) => handleStart(e, 'end', line.id)}
                                                />
                                                <circle
                                                    cx={line.toX}
                                                    cy={line.toY}
                                                    r={15 * scale}
                                                    fill="transparent"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={(e) => handleStart(e, 'end', line.id)}
                                                    onTouchStart={(e) => handleStart(e, 'end', line.id)}
                                                />
                                            </>
                                        )}
                                    </g>
                                );
                            })}

                            {/* Connection point for shot lines (basket) */}
                            {dragging.type === 'end' && currentFrame.lines.find((l) => l.id === dragging.id)?.type === 'shot' && (
                                (() => {
                                    const basketPoints = courtType === 'halfcourt'
                                        ? [{ x: 270, y: 85 }] // Half court: single basket
                                        : [
                                            { x: 80, y: 274 },  // Full court: left basket
                                            { x: 830, y: 274 }  // Full court: right basket
                                        ];

                                    return basketPoints.map((basket, index) => (
                                        <g key={`basket-highlight-${index}`}>
                                            <circle
                                                cx={basket.x}
                                                cy={basket.y}
                                                r={8 * scale}
                                                fill="#F0A85F"
                                                stroke="#DD7720"
                                                strokeWidth={1}
                                                strokeDasharray="4,2"
                                            >
                                                <animate attributeName="fill" values="#F0A85F;#F6C28C;#F0A85F" dur="2s" repeatCount="indefinite" />
                                                <animate attributeName="stroke-dashoffset" from="0" to="6" dur="2s" repeatCount="indefinite" />
                                            </circle>
                                        </g>
                                    ));
                                })()
                            )}

                            {/* Connection points for dashed lines (other players' last lines) */}
                            {isDraggingLineEnd && currentFrame.lines.find((l) => l.id === dragging.id)?.type !== 'shot' &&
                                currentFrame.lines
                                    .filter((line) => {
                                        const draggedLine = currentFrame.lines.find((l) => l.id === dragging.id && (l.type === 'dashed' || l.type === 'handoff'));
                                        return (
                                            draggedLine &&
                                            line.fromId !== draggedLine.fromId &&
                                            ['straight', 'bar', 'zigzag'].includes(line.type) &&
                                            getPlayerLinesInOrder(currentFrame.lines, line.fromId).slice(-1)[0]?.id === line.id
                                        );
                                    })
                                    .map((line) => (
                                        <g key={`highlight-${line.id}`}>
                                            <circle
                                                cx={line.toX}
                                                cy={line.toY}
                                                r={8 * scale}
                                                fill="#F0A85F"
                                                stroke="#DD7720"
                                                strokeWidth={1}
                                                strokeDasharray="4,2"
                                            >
                                                <animate attributeName="fill" values="#F0A85F;#F6C28C;#F0A85F" dur="2s" repeatCount="indefinite" />
                                                <animate attributeName="stroke-dashoffset" from="0" to="6" dur="2s" repeatCount="indefinite" />
                                            </circle>
                                        </g>
                                    ))}
                        </svg>
                    </Box>

                    <Grid sx={{ textAlign: 'center', mt: isPortrait ? 1 : 1 }} xs={12}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: isPortrait ? 'space-between' : 'center',
                            alignItems: 'center',
                            gap: isPortrait ? 1 : 1,
                            mt: isPortrait ? 0 : 1.5,
                            px: isPortrait ? 1 : 0,
                        }}>
                            {/* Left side: Court type + visibility toggle */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ToggleButtonGroup
                                    value={courtType}
                                    exclusive
                                    onChange={handleCourtChange}
                                    aria-label="Court Type"
                                    size="small"
                                    sx={{
                                        height: isPortrait ? 26 : 32,
                                        backgroundColor: '#ffffff',
                                        '& .MuiToggleButton-root': {
                                            fontSize: isPortrait ? '9px' : '12px',
                                            px: isPortrait ? 1 : 2,
                                            py: isPortrait ? 0.5 : 1,
                                            color: '#000000',
                                            borderColor: 'rgba(0, 0, 0, 0.12)',
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                color: '#000000',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                }
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value="halfcourt">
                                        {t('PlayBuilder09')}
                                    </ToggleButton>
                                    <ToggleButton value="fullcourt">
                                        {t('PlayBuilder10')}
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                {isPortrait && (
                                    <ToggleButton
                                        value={showDescription}
                                        selected={showDescription}
                                        onChange={() => setShowDescription(!showDescription)}
                                        aria-label="Toggle Description Visibility"
                                        size="small"
                                        sx={{
                                            ...commonStyles.card,
                                            height: 26,
                                            width: 26,
                                            minWidth: 0,
                                            p: 0,
                                            backgroundColor: '#ffffff',
                                            color: '#000000',
                                            borderColor: 'rgba(0, 0, 0, 0.12)',
                                            '&.Mui-selected': {
                                                backgroundColor: '#00398f',
                                                color: '#ffffff',
                                                '&:hover': {
                                                    backgroundColor: '#002d6b',
                                                }
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                                transform: 'translateY(-1px)',
                                            }
                                        }}
                                    >
                                        {showDescription ? <VisibilityIcon sx={{ fontSize: '16px' }} /> : <VisibilityOffIcon sx={{ fontSize: '16px' }} />}
                                    </ToggleButton>
                                )}
                            </Box>

                            {/* Right side: Zoom buttons */}
                            {isPortrait && (currentFrame.players.length > 0 || currentFrame.cones.length > 0) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1.8, backgroundColor: '#ffffff', borderRadius: 1, px: 0.5 }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#000000' }}>
                                        Zoom
                                    </Typography>
                                    <IconButton
                                        onClick={() => setScale(Math.max(1.0, scale - 0.1))}
                                        disabled={scale <= 1.0}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: scale > 1.0 ? '#00398f' : '#f0f0f0',
                                            color: scale > 1.0 ? '#ffffff' : '#9e9e9e',
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            '&:hover': {
                                                bgcolor: scale > 1.0 ? '#002d6b' : '#f0f0f0',
                                            },
                                            '&:disabled': {
                                                bgcolor: '#f0f0f0',
                                                color: '#bdbdbd',
                                                border: '1px solid rgba(0, 0, 0, 0.06)',
                                            }
                                        }}
                                    >
                                        <RemoveRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setScale(Math.min(1.5, scale + 0.1))}
                                        disabled={scale >= 1.5}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: scale < 1.5 ? '#00398f' : '#f0f0f0',
                                            color: scale < 1.5 ? '#ffffff' : '#9e9e9e',
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            '&:hover': {
                                                bgcolor: scale < 1.5 ? '#002d6b' : '#f0f0f0',
                                            },
                                            '&:disabled': {
                                                bgcolor: '#f0f0f0',
                                                color: '#bdbdbd',
                                                border: '1px solid rgba(0, 0, 0, 0.06)',
                                            }
                                        }}
                                    >
                                        <AddRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>

                        {/* Frame Description Input - Only show in portrait if showDescription is true */}
                        {(!isPortrait || (isPortrait && showDescription)) && (
                            <Grid
                                sx={{ mt: isPortrait ? 0.5 : 1.5, px: isPortrait ? 1 : 2 }}
                                xs={12}>

                                <Box sx={{
                                    ...commonStyles.card,
                                    padding: 0.75,
                                    '&:focus-within': {
                                        ...commonStyles.cardHover,
                                    }
                                }}>
                                    <textarea
                                        maxLength={250}
                                        value={currentFrame.description || ''}
                                        onChange={handleDescriptionChange}
                                        style={{
                                            width: '100%',
                                            height: isPortrait ? '80px' : '70px',
                                            padding: '10px',
                                            fontSize: isPortrait ? '16px' : '13px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            outline: 'none',
                                            resize: 'none',
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: '1.4',
                                            fontFamily: 'Roboto, Arial, sans-serif',
                                            backgroundColor: 'transparent',
                                            overflowY: 'auto',
                                        }}
                                        placeholder={nativeApp ? '' : t('PlayBuilder14')}
                                    />
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        textAlign: 'right',
                                        mt: 0.75,
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {(currentFrame.description || '').length}/250
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {/* Right Sidebar */}
                <Grid
                    sx={{
                        p: isPortrait ? 1 : 2,
                        pb: isPortrait ? 7 : 2,
                        borderLeft: isPortrait ? 'none' : '1px solid rgba(0,0,0,0.08)',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        backdropFilter: isPortrait ? 'none' : 'blur(10px)',
                        order: isPortrait ? 3 : 0,
                    }}
                    xs={isPortrait ? 12 : 3}>
                    <Box>
                        {/* <Divider sx={{ my: isPortrait ? 1 : 3, display: selectedLineId !== null && !isPortrait ? 'none' : 'block' }} /> */}
                        <PlayBuilderActionButtons
                            selectedPlayer={currentFrame.players.find((p) => p.id === selectedPlayerId) || null}
                            createLine={createLine}
                            handleCreateNewFrame={handleCreateNewFrame}
                            setIsModalOpen={setIsModalOpen}
                            handleDeleteLine={handleDeleteLine}
                            handleToggleBall={handleToggleBall}
                            handleSavePlay={handleSavePlay}
                            handleColorChange={handleColorChange}
                            handleHandoffColorChange={handleHandoffColorChange}
                            colorOptions={colorOptions}
                            selectedLine={selectedLine}
                            toggleShape={toggleShape}
                            isDoubleBezier={isDoubleBezier}
                            selectedLineId={selectedLineId}
                            handleAddOffensivePlayer={handleAddOffensivePlayer}
                            handleAddDefender={handleAddDefender}
                            handleAddSpecialDefender={handleAddSpecialDefender}
                            handleDeletePlayer={handleDeletePlayer}
                            currentFrame={currentFrame}
                            selectedPlayerId={selectedPlayerId}
                            hasConnectedLines={
                                selectedPlayerId !== null &&
                                currentFrame.lines.some((l) => l.fromId === selectedPlayerId || l.toId === selectedPlayerId)
                            }
                            handleAddCone={handleAddCone}
                            handleDeleteCone={handleDeleteCone}
                            selectedConeId={selectedConeId}
                            selectedConeColor={(currentFrame.cones || []).find((c) => c.id === selectedConeId)?.color ?? '#000000'}
                            handleSetSelectedConeColor={handleSetSelectedConeColor}
                            handleAddTextBox={handleAddTextBox}
                            selectedTextBoxId={selectedTextBoxId}
                            selectedTextBoxFontSize={(currentFrame.texts || []).find((tb) => tb.id === selectedTextBoxId)?.fontSize ?? 14}
                            handleSetSelectedTextBoxFontSize={handleSetSelectedTextBoxFontSize}
                            handleDeleteSelectedTextBox={handleDeleteSelectedTextBox}
                            selectedShadeId={selectedShadeId}
                            selectedShadeColor={(currentFrame.shades || []).find((s) => s.id === selectedShadeId)?.color || '#38BDF8'}
                            handleSetSelectedShadeColor={handleSetSelectedShadeColor}
                            handleAddShadeCircle={() => handleAddShade('circle')}
                            handleAddShadeRect={() => handleAddShade('rect')}
                            handleDeleteSelectedShade={handleDeleteSelectedShade}
                            handlePlayerColorChange={handlePlayerColorChange}
                            handlePlayerNumberChange={handlePlayerNumberChange}
                            portrait={isPortrait}
                        />
                    </Box>

                    {currentFrame.lines.length > 0 && !isPortrait && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ my: 1 }} />
                            <PlayAnimationLinesOrder
                                lines={currentFrame.lines}
                                players={currentFrame.players}
                                onUpdateLines={(updatedLines) =>
                                    setFrames((prev) => {
                                        const newFrames = [...prev];
                                        newFrames[currentFrameIndex] = { ...newFrames[currentFrameIndex], lines: updatedLines };
                                        return newFrames;
                                    })
                                }
                                onLineHover={setHoveredLineId}
                            />
                        </Box>
                    )}
                </Grid>
            </Grid >

            {/* Bottom App Bar for Portrait Mode */}
            {
                isPortrait && (
                    <PlayBuilderBottomBar
                        handleAddOffensivePlayer={handleAddOffensivePlayer}
                        handleAddDefender={handleAddDefender}
                        handleAddSpecialDefender={handleAddSpecialDefender}
                        handleAddCone={handleAddCone}
                        handleAddTextBox={handleAddTextBox}
                        handleAddShadeCircle={() => handleAddShade('circle')}
                        handleAddShadeRect={() => handleAddShade('rect')}
                        lines={currentFrame.lines}
                        players={currentFrame.players}
                        onUpdateLines={(updatedLines) =>
                            setFrames((prev) => {
                                const newFrames = [...prev];
                                newFrames[currentFrameIndex] = { ...newFrames[currentFrameIndex], lines: updatedLines };
                                return newFrames;
                            })
                        }
                        nativeApp={nativeApp}
                    />
                )
            }

            {/* <LanguageSwitcher /> */}
        </>
    );

    return (
        <>
            <Box sx={{ width: '100%', height: '100vh', overflow: 'auto' }}>
                {dialogContent}
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar({ open: false, message: '' })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ '& .MuiSnackbarContent-root': { backgroundColor: '#00398F' } }}
            />
            <Dialog
                open={isSignInPromptOpen}
                onClose={() => setIsSignInPromptOpen(false)}
                aria-labelledby="signin-to-save-title"
                PaperProps={{
                    style: {
                        borderRadius: 15,
                    },
                }}
            >
                <DialogTitle
                    id="signin-to-save-title"
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 700,
                        color: playMainTitle,
                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        border: `1px solid ${playBorder}`,
                        padding: '8px 16px',
                        minHeight: '48px',
                    }}
                >
                    {t('PlayBuilder22')}
                    <IconButton
                        aria-label="close"
                        onClick={() => setIsSignInPromptOpen(false)}
                        edge="end"
                        sx={{ color: playMainTitle }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        marginTop: '20px',
                        paddingBottom: '16px',
                        paddingTop: '4px',
                        color: playMainTitle,
                    }}
                >
                    <Typography sx={{ color: 'inherit' }}>
                        {t('PlayBuilder23')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, pt: 1.5 }}>
                    <Button
                        onClick={() => setIsSignInPromptOpen(false)}
                        variant="text"
                        sx={{
                            textTransform: 'none',
                            borderColor: playMainTitle,
                            color: playMainTitle,
                            '&:hover': {
                                borderColor: playMainTitle,
                                backgroundColor: 'rgba(0,57,143,0.06)',
                            },
                        }}
                    >
                        {t('PlayBuilder24')}
                    </Button>
                    <Button
                        onClick={() => {
                            window.location.href = app;
                        }}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00398F',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: 15,
                            border: `1px solid rgba(33, 150, 243, 0.4)`,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#002d6b',
                            },
                        }}
                    >
                        {t('PlayBuilder25')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isPdfNameDialogOpen}
                onClose={() => setIsPdfNameDialogOpen(false)}
                aria-labelledby="pdf-name-title"
                sx={isPortrait ? {
                    '& .MuiDialog-container': {
                        alignItems: 'flex-start',
                        paddingTop: '16px',
                    },
                } : {}}
                PaperProps={{
                    style: {
                        borderRadius: 15,
                    },
                    sx: isPortrait ? {
                        margin: '16px auto 0',
                        maxHeight: 'calc(100vh - 32px)',
                    } : {}
                }}
            >
                <DialogTitle
                    id="pdf-name-title"
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 700,
                        color: playMainTitle,
                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        border: `1px solid ${playBorder}`,
                        padding: '8px 16px',
                        minHeight: '48px',
                    }}
                >
                    {t('PlayBuilder19')}
                    <IconButton
                        aria-label="close"
                        onClick={() => setIsPdfNameDialogOpen(false)}
                        edge="end"
                        sx={{ color: playMainTitle }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        marginTop: '20px',
                        paddingBottom: '16px',
                        paddingTop: '4px',
                        color: playMainTitle,
                    }}
                >
                    <TextField
                        autoFocus
                        fullWidth
                        value={pdfPlayName}
                        onChange={(e) => setPdfPlayName(e.target.value)}
                        margin="dense"
                        placeholder="Play name"
                    />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {t('Profile39')}
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            value={pdfUse3DPlayers ? '3d' : 'classic'}
                            onChange={(_, value) => {
                                if (value === null) return;
                                setPdfUse3DPlayers(value === '3d');
                            }}
                            sx={{
                                display: 'flex',
                                gap: 1,
                                '& .MuiToggleButton-root': {
                                    flex: 1,
                                    borderRadius: 1.5,
                                    p: 0.75,
                                    minHeight: 0,
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper,
                                    transition: 'all 0.2s ease-in-out',
                                    '&.Mui-selected': {
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.08)'
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="classic">
                                <Stack spacing={0.5} alignItems="center" width="100%">
                                    <Box
                                        component="img"
                                        src="/images/playbuilder/NormalPlayersPDF.png"
                                        alt={t('Profile39')}
                                        sx={{
                                            width: '100%',
                                            maxHeight: 40,
                                            borderRadius: 1,
                                            objectFit: 'contain',
                                            objectPosition: 'center',
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    />
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        {t('Profile40')}
                                    </Typography>
                                </Stack>
                            </ToggleButton>
                            <ToggleButton value="3d">
                                <Stack spacing={0.5} alignItems="center" width="100%">
                                    <Box
                                        component="img"
                                        src="/images/playbuilder/3DPlayersPDF.png"
                                        alt={t('Profile39')}
                                        sx={{
                                            width: '100%',
                                            maxHeight: 40,
                                            borderRadius: 1,
                                            objectFit: 'contain',
                                            objectPosition: 'center',
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    />
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        {t('Profile41')}
                                    </Typography>
                                </Stack>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, pt: 1.5 }}>
                    <Button onClick={() => setIsPdfNameDialogOpen(false)} variant="text" sx={{ textTransform: 'none' }}>
                        {t('PlayBuilder20')}
                    </Button>
                    <Button
                        onClick={handleConfirmDownloadPdf}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00398F',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: 15,
                            border: `1px solid rgba(33, 150, 243, 0.4)`,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#002d6b',
                            },
                        }}
                    >
                        {t('PlayBuilder21')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isConfirmExitDialogOpen}
                onClose={() => setIsConfirmExitDialogOpen(false)}
                aria-labelledby="confirm-exit-title"
                PaperProps={{
                    style: {
                        borderRadius: 15,
                    },
                }}
            >
                <DialogTitle
                    id="confirm-exit-title"
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 700,
                        color: playMainTitle,
                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        border: `1px solid ${playBorder}`,
                        padding: '8px 16px',
                        minHeight: '48px',
                    }}
                >
                    Confirm exit
                    <IconButton
                        aria-label="close"
                        onClick={() => setIsConfirmExitDialogOpen(false)}
                        edge="end"
                        sx={{ color: playMainTitle }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        marginTop: '20px',
                        paddingBottom: '16px',
                        paddingTop: '4px',
                        color: playMainTitle,
                    }}
                >
                    <Typography sx={{ color: 'inherit' }}>
                        Are you sure you want to go back?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, pt: 1.5 }}>
                    <Button
                        onClick={() => setIsConfirmExitDialogOpen(false)}
                        variant="text"
                        sx={{
                            textTransform: 'none',
                            borderColor: playMainTitle,
                            color: playMainTitle,
                            '&:hover': {
                                borderColor: playMainTitle,
                                backgroundColor: 'rgba(0,57,143,0.06)',
                            },
                        }}
                    >
                        Keep editing
                    </Button>
                    <Button
                        onClick={() => {
                            setIsConfirmExitDialogOpen(false);
                            navigate(-1);
                        }}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00398F',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: 15,
                            border: `1px solid rgba(33, 150, 243, 0.4)`,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#002d6b',
                            },
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            {
                isModalOpen && (
                    <DialogPlayAnimation
                        play={{ title: props.title || 'Play' }}
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        frames={frames}
                        courtType={courtType}
                        nativeApp={nativeApp}
                        use3DPlayers={use3DPlayers}
                        courtColors={courtColors}
                    />
                )
            }
        </>
    );
}